import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../Auth/AuthContext';
import {
    listLibraryItems,
    uploadImageToLibrary,
    uploadVideoToLibrary,
    deleteLibraryItem as apiDeleteLibraryItem,
    createLibraryItem,
} from '../../utils/mediaLibraryApi';
import {
    searchGiphy,
    getGiphyTrending,
    createSocialPost,
    listSocialPosts,
    updateSocialPost,
    deleteSocialPost,
} from '../../utils/socialApi';
import { fetchStoreProducts } from '../../utils/shopifyApi';
import { getApiBaseUrl, getSeoBaseUrl } from '../../utils/api';
import { ProductCollage } from '../Checkout/ProductCollage';
import { toast } from '../Toast';
import GlobalModal from '../Common/GlobalModal';

// ============================================================================
// InstagramHome — KevoSocial-inspired Instagram scheduler home page.
// Library is fully backend-driven via /api/media-library. The calendar starts
// empty; posts/stories will be wired to /api/social-posts in a follow-up.
// ============================================================================

/** Given a server SocialPost and the currently-visible weekStart, derive the
 *  local calendar-cell shape: dayOffset (0..6), hour (7..18), embedded media,
 *  status, serverId. Posts outside the week still return (dayOffset may be
 *  out of range) — the caller filters. */
function serverPostToLocal(post, weekStart) {
    const scheduled = post.scheduledAt ? new Date(post.scheduledAt) : null;
    const media0 = Array.isArray(post.media) && post.media[0] ? post.media[0] : null;
    // Days from weekStart (floored to date boundaries, timezone-safe).
    const weekStartDay = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate()).getTime();
    const scheduledDay = scheduled ? new Date(scheduled.getFullYear(), scheduled.getMonth(), scheduled.getDate()).getTime() : weekStartDay;
    const dayOffset = Math.round((scheduledDay - weekStartDay) / (24 * 3600 * 1000));
    // Preserve the precise minute the user picked. The composer supports
    // arbitrary minutes (1-min granularity), so rounding here would silently
    // overwrite the user's chosen time on every reload/edit.
    let hourFloat = 10;
    if (scheduled) {
        hourFloat = scheduled.getHours() + scheduled.getMinutes() / 60;
    }
    // Server may not preserve full product metadata; merge from local cache.
    const cachedMedia = getMediaForPost(post.id);
    const finalMedia = (media0 && media0.url)
        ? { ...cachedMedia, ...media0 }
        : (cachedMedia || media0 || null);
    return {
        id: `srv-${post.id}`,
        serverId: post.id,
        status: post.status,
        caption: post.caption || '',
        mediaId: null,
        media: finalMedia,
        dayOffset,
        hour: hourFloat,
        scheduledAt: post.scheduledAt || null,
        errorMessage: post.errorMessage || null,
        frameId: getFrameForPost(post.id) || 'none',
    };
}

/** Converts a fractional hour (e.g. 14.5) into [hours, minutes] for Date.setHours. */
function hourToHM(h) {
    const hour = Math.floor(h);
    const minutes = Math.round((h - hour) * 60);
    return [hour, minutes];
}

// --- Frame side-table -------------------------------------------------------
// The backend's social_post table doesn't store frameId yet, so we persist
// `serverId -> frameId` in localStorage. Survives refresh, scoped per browser.
const FRAME_MAP_KEY = 'ig_post_frames_v1';
function readFrameMap() {
    try { return JSON.parse(localStorage.getItem(FRAME_MAP_KEY) || '{}'); } catch { return {}; }
}
function setFrameForPost(serverId, frameId) {
    if (!serverId) return;
    const map = readFrameMap();
    if (frameId && frameId !== 'none') map[serverId] = frameId;
    else delete map[serverId];
    try { localStorage.setItem(FRAME_MAP_KEY, JSON.stringify(map)); } catch {}
}
function getFrameForPost(serverId) {
    if (!serverId) return null;
    return readFrameMap()[serverId] || null;
}

// --- Media side-table ------------------------------------------------------
// Mirrors the frame side-table. The backend may not roundtrip the full
// product image (url/title), so we cache it locally keyed by serverId and
// fall back to it when post.media is empty.
const MEDIA_MAP_KEY = 'ig_post_media_v1';
function readMediaMap() {
    try { return JSON.parse(localStorage.getItem(MEDIA_MAP_KEY) || '{}'); } catch { return {}; }
}
function setMediaForPost(serverId, media) {
    if (!serverId) return;
    const map = readMediaMap();
    if (media && media.url) {
        const cached = {
            url: media.url,
            type: media.type || 'image',
            title: media.title || media.productTitle || '',
            // Keep the original product URL too — re-edits use this so frames
            // composite onto the source image, not a previously framed one.
            productImageUrl: media.productImageUrl || media.url,
        };
        // Persist Quick Checkout bundle info so the edit modal can re-render
        // the collage layout (instead of the baked image) and re-bake on save.
        // `code` is what we hand the auto-DM dispatcher as linkInBio so the
        // recipient's tap target is the SEO checkout page, not a stale URL.
        if (media.isBundle && media.bundle) {
            cached.isBundle = true;
            cached.bundle = {
                id: media.bundle.id,
                name: media.bundle.name || '',
                code: media.bundle.code || '',
                collageTemplate: media.bundle.collageTemplate || 'bundle',
                products: (media.bundle.products || []).map((p) => ({
                    id: p.id,
                    name: p.name,
                    imageUrl: p.imageUrl,
                    price: p.price,
                })),
            };
        }
        map[serverId] = cached;
    } else {
        delete map[serverId];
    }
    try { localStorage.setItem(MEDIA_MAP_KEY, JSON.stringify(map)); } catch {}
}
function getMediaForPost(serverId) {
    if (!serverId) return null;
    return readMediaMap()[serverId] || null;
}

/** Normalize a server MediaLibraryItem into the client-side shape consumed by
 *  the library + composer. `id` is prefixed so it can't collide with sample
 *  ids and numeric/string handling is uniform. */
function normalizeLibraryItem(item) {
    return {
        id: `lib-${item.id}`,
        serverId: item.id,
        source: 'upload',
        type: item.type,
        url: item.url,
        embedUrl: item.embedUrl || null,
        thumbnailUrl: item.thumbnailUrl || null,
        cloudflareId: item.cloudflareId || null,
        cloudflareUid: item.cloudflareUid || null,
        title: item.title || 'Untitled',
        width: item.width || null,
        height: item.height || null,
    };
}

const DAY_LABELS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
// Full 24-hour day in 30-minute slots: 0.0, 0.5, 1.0, ..., 23.5.
// Integer = :00, .5 = :30. Used by the calendar grid AND the schedule dropdown.
const HOURS = Array.from({ length: 48 }, (_, i) => i * 0.5);

function startOfWeek(date) { const d = new Date(date); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); return d; }
/** "Today centered" week start — returns today − 3 days so the 7-day grid
 *  renders as [today-3, today-2, today-1, TODAY, today+1, today+2, today+3]. */
function centeredWeekStart(date = new Date()) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 3);
    return d;
}
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
function formatRange(start) { const end = addDays(start, 6); return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { day: 'numeric' })}, ${start.getFullYear()}`; }
function formatHour(h) {
    const hour = Math.floor(h);
    const min = Math.round((h - hour) * 60);
    const s = hour >= 12 ? 'PM' : 'AM';
    const hh = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${String(hh).padStart(2, '0')}:${String(min).padStart(2, '0')} ${s}`;
}

// --- MediaThumb ---
// Library items are always real uploads now — render the Cloudflare URL, with
// a thumbnail fallback for videos.
// Renders a bundle's saved ProductCollage layout filling the parent (which is
// expected to be position:relative). Measures the available height with a
// ResizeObserver because ProductCollage uses pixel arithmetic for the
// 'stacked'/'polaroid'/'carousel' templates.
function BundleCollageFill({ products, template }) {
    const ref = useRef(null);
    const [h, setH] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const ro = new ResizeObserver(([entry]) => {
            if (entry?.contentRect?.height) setH(entry.contentRect.height);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return (
        <div ref={ref} style={{ position: 'absolute', inset: 0, background: '#fafaf9', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {h > 0 && (
                <div style={{ width: '100%' }}>
                    <ProductCollage
                        products={products}
                        template={template || 'bundle'}
                        height={h}
                        padding={Math.max(4, Math.round(h * 0.05))}
                        radius={Math.max(4, Math.round(h * 0.05))}
                    />
                </div>
            )}
        </div>
    );
}

function MediaThumb({ media, emojiSize = 18 }) {
    if (!media) return null;
    if (media.isBundle && media.bundle) {
        return <BundleCollageFill products={media.bundle.products || []} template={media.bundle.collageTemplate} />;
    }
    const isVideo = media.type === 'video';
    const src = isVideo ? (media.thumbnailUrl || null) : (media.url || null);
    if (!src) {
        return (
            <div style={{ position: 'absolute', inset: 0, background: '#1f2937', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: emojiSize }}>
                {isVideo ? '▶' : '🖼'}
            </div>
        );
    }
    return <img src={src} alt={media.title || ''} draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />;
}

// --- LibraryPanel (sidebar) ---
// Shows a paginated list of products fetched from the user's connected Shopify
// store. Each tile is draggable onto a calendar slot — the dragged payload is
// shaped like a media item (id, title, type:'image', url) so the existing
// post-creation flow keeps working.
function LibraryPanel({
    onPickMedia, onNewPost, draggingId, onDragStart, onDragEnd,
    accessToken, onAddGiphyToLibrary,
}) {
    const PAGE_SIZE = 12;
    const [activeTab, setActiveTab] = useState('products'); // 'products' | 'giphy'

    /* ── Products tab ── */
    const [products, setProducts] = useState([]);
    const [endCursor, setEndCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const inFlightRef = useRef(false);

    /* Quick Checkout Links bundles — surfaced in the same Products grid as
       Shopify products. Marked with a small "BUNDLE" badge so the user can
       tell them apart from single-product tiles. */
    const [bundles, setBundles] = useState([]);

    /* ── Giphy tab ── */
    const [giphyQuery, setGiphyQuery] = useState('');
    const [giphyResults, setGiphyResults] = useState([]);
    const [giphyLoading, setGiphyLoading] = useState(false);
    const [giphyError, setGiphyError] = useState(null);
    const [addingGifId, setAddingGifId] = useState(null);
    const giphyDebounceRef = useRef(null);
    const giphyInFlightRef = useRef(0); // request token to ignore stale responses

    const productToMedia = (p) => ({
        id: String(p.id ?? p.productId ?? p.title),
        title: p.title || 'Product',
        type: 'image',
        url: p.images?.[0]?.src || p.featuredImage?.url || p.imageUrl || '',
        isProduct: true,
        product: p,
    });

    const bundleToMedia = (b) => {
        const og = (b.ogImage || '').trim();
        const fromProduct = (b.products || []).find((p) => p?.imageUrl)?.imageUrl;
        return {
            id: `bundle-${b.id}`,
            title: b.name || 'Bundle',
            type: 'image',
            url: og || fromProduct || '',
            isBundle: true,
            bundle: b,
        };
    };

    const combinedMedia = useMemo(
        () => [...bundles.map(bundleToMedia), ...products.map(productToMedia)],
        [bundles, products],
    );

    const loadProducts = useCallback(async ({ append }) => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        if (append) setLoadingMore(true); else setLoading(true);
        setError(null);
        const res = await fetchStoreProducts({
            first: PAGE_SIZE,
            after: append ? endCursor : undefined,
            accessToken,
        });
        inFlightRef.current = false;
        if (res.ok) {
            const newItems = res.data?.products || [];
            setProducts((prev) => append ? [...prev, ...newItems] : newItems);
            setEndCursor(res.data?.pageInfo?.endCursor || null);
            setHasMore(!!res.data?.pageInfo?.hasNextPage);
        } else {
            setError(res.error || 'Failed to load products');
            if (!append) { setProducts([]); setHasMore(false); }
        }
        if (append) setLoadingMore(false); else setLoading(false);
    }, [accessToken, endCursor]);

    useEffect(() => {
        loadProducts({ append: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Load Quick Checkout Links once. They're typically few — no pagination. */
    useEffect(() => {
        if (!accessToken) return;
        let cancelled = false;
        (async () => {
            try {
                const base = (getApiBaseUrl() || '').replace(/\/$/, '');
                const res = await fetch(`${base}/api/checkout-links`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const json = await res.json();
                if (cancelled) return;
                if (json?.success) {
                    setBundles(Array.isArray(json.data?.links) ? json.data.links : []);
                }
            } catch { /* bundles are optional — fail silently */ }
        })();
        return () => { cancelled = true; };
    }, [accessToken]);

    /* Giphy: trending on first open + debounced search on query change.
       Token-based stale-response guard so a fast typer doesn't see flicker. */
    useEffect(() => {
        if (activeTab !== 'giphy') return;
        if (giphyDebounceRef.current) clearTimeout(giphyDebounceRef.current);
        giphyDebounceRef.current = setTimeout(async () => {
            const token = ++giphyInFlightRef.current;
            setGiphyLoading(true);
            setGiphyError(null);
            const q = giphyQuery.trim();
            const res = q
                ? await searchGiphy({ q, limit: 24, accessToken })
                : await getGiphyTrending({ limit: 24, accessToken });
            if (token !== giphyInFlightRef.current) return; // a newer request superseded
            if (res.ok) {
                setGiphyResults(Array.isArray(res.data?.results) ? res.data.results : []);
            } else {
                setGiphyError(res.error || 'Failed to load GIFs');
                setGiphyResults([]);
            }
            setGiphyLoading(false);
        }, 250);
        return () => { if (giphyDebounceRef.current) clearTimeout(giphyDebounceRef.current); };
    }, [activeTab, giphyQuery, accessToken]);

    const handleGifClick = async (gif) => {
        if (!onAddGiphyToLibrary || addingGifId) return;
        setAddingGifId(gif.id);
        try { await onAddGiphyToLibrary(gif); }
        finally { setAddingGifId(null); }
    };

    const TABS = [
        { id: 'products', label: 'Products' },
        { id: 'giphy', label: 'Giphy' },
    ];

    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ padding: '20px 20px 12px', borderBottom: '1px solid #f5f5f4', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                    <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', margin: 0, letterSpacing: '-0.02em' }}>Library</h2>
                    <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e' }}>
                        {activeTab === 'products'
                            ? `${combinedMedia.length} ${combinedMedia.length === 1 ? 'Item' : 'Items'}`
                            : `${giphyResults.length} GIFs`}
                    </span>
                </div>
                <p style={{ fontSize: 12, color: '#78716c', lineHeight: 1.5, margin: '0 0 12px' }}>Drag a product onto a time slot, or create a post.</p>
                <button onClick={onNewPost} style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff',
                    backgroundColor: '#1f2937', border: 'none', cursor: 'pointer',
                    boxShadow: '0 6px 14px -6px rgba(0,0,0,0.4)', marginBottom: 10,
                }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    New Post
                </button>

                {/* Tab switcher: Products | Giphy — same sliding pill style as
                    the Feed/Stories chip in CalendarToolbar so the calendar UI
                    keeps a consistent feel. */}
                <div style={{ display: 'flex', alignItems: 'center', background: '#f5f5f4', borderRadius: 99, padding: 3, position: 'relative' }}>
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            style={{
                                position: 'relative', zIndex: 2, flex: 1, padding: '6px 16px',
                                fontSize: 12, fontWeight: 600, borderRadius: 99, border: 'none',
                                background: 'transparent',
                                color: activeTab === t.id ? '#fff' : '#78716c',
                                cursor: 'pointer', transition: 'color 200ms',
                            }}
                        >{t.label}</button>
                    ))}
                    <div style={{
                        position: 'absolute', top: 3, bottom: 3, borderRadius: 99,
                        background: '#1f2937', transition: 'all 300ms ease',
                        left: activeTab === 'products' ? 3 : '50%',
                        width: 'calc(50% - 3px)',
                        boxShadow: '0 4px 10px -4px rgba(0,0,0,0.3)',
                    }} />
                </div>

                {/* Giphy search bar — only on Giphy tab */}
                {activeTab === 'giphy' && (
                    <div style={{ position: 'relative', marginTop: 10 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a8a29e" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            value={giphyQuery}
                            onChange={(e) => setGiphyQuery(e.target.value)}
                            placeholder="Search GIFs..."
                            style={{ width: '100%', fontSize: 12, padding: '8px 12px 8px 32px', borderRadius: 8, border: '1px solid #e7e5e4', background: '#fff', outline: 'none', boxSizing: 'border-box' }}
                            onFocus={(e) => e.target.style.borderColor = '#1f2937'}
                            onBlur={(e) => e.target.style.borderColor = '#e7e5e4'}
                        />
                    </div>
                )}
            </div>

            {/* Scroll container — switches body based on active tab */}
            <div
                className="ig-scroll"
                onScroll={(e) => {
                    if (activeTab !== 'products') return;
                    if (loading || loadingMore || !hasMore) return;
                    const el = e.currentTarget;
                    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
                        loadProducts({ append: true });
                    }
                }}
                style={{ padding: 16, paddingTop: 12, flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
            >
                {activeTab === 'products' ? (
                    <>
                        {error && (
                            <div style={{ fontSize: 11, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                                {error}
                            </div>
                        )}

                        {loading && combinedMedia.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '28px 8px' }}>
                                <span style={{ display: 'inline-block', width: 18, height: 18, border: '2.5px solid rgba(0,0,0,0.15)', borderTopColor: '#111827', borderRadius: '50%', animation: 'igSpin 0.7s linear infinite' }} />
                                <div style={{ fontSize: 11, color: '#78716c', marginTop: 8, fontStyle: 'italic' }}>Loading products…</div>
                            </div>
                        ) : combinedMedia.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '24px 8px' }}>
                                <div style={{ fontSize: 24, marginBottom: 6, opacity: 0.5 }}>🛍️</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#44403c' }}>No products found</div>
                                <div style={{ fontSize: 11, marginTop: 2, fontStyle: 'italic', color: '#78716c' }}>Connect a Shopify store or create a Quick Checkout Link.</div>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                {combinedMedia.map((media) => {
                                    const imageUrl = media.url;
                                    const title = media.title;
                                    const id = media.id;
                                    const isBundle = !!media.isBundle;
                                    return (
                                        <div
                                            key={id}
                                            draggable
                                            onDragStart={(e) => { e.stopPropagation(); onDragStart?.(e, media); }}
                                            onDragEnd={onDragEnd}
                                            onClick={() => onPickMedia?.(media)}
                                            title={`${title} — ${isBundle ? 'bundle, ' : ''}click to compose, drag to schedule`}
                                            style={{
                                                aspectRatio: '1', borderRadius: 8, position: 'relative', overflow: 'hidden',
                                                border: '1px solid rgba(214,211,209,0.7)', cursor: 'grab', background: '#f5f5f4',
                                                boxShadow: '0 2px 4px -1px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.7)',
                                                opacity: draggingId === id ? 0.4 : 1,
                                                transform: draggingId === id ? 'rotate(-2deg) scale(0.97)' : 'none',
                                                transition: 'transform 280ms cubic-bezier(.2,.9,.2,1.2), box-shadow 280ms ease, opacity 280ms ease',
                                            }}
                                            onMouseEnter={(e) => { if (draggingId !== id) e.currentTarget.style.transform = 'translateY(-4px) rotate(-1.5deg) scale(1.03)'; }}
                                            onMouseLeave={(e) => { if (draggingId !== id) e.currentTarget.style.transform = 'none'; }}
                                        >
                                            {isBundle ? (
                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafaf9' }}>
                                                    <div style={{ width: '100%' }}>
                                                        <ProductCollage
                                                            products={media.bundle?.products || []}
                                                            template={media.bundle?.collageTemplate || 'bundle'}
                                                            height={140}
                                                            padding={6}
                                                            radius={6}
                                                        />
                                                    </div>
                                                </div>
                                            ) : imageUrl ? (
                                                <img src={imageUrl} alt={title} draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a8a29e', fontSize: 24 }}>🛍️</div>
                                            )}
                                            {isBundle && (
                                                <div style={{ position: 'absolute', top: 4, left: 4, display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 8, fontWeight: 700, padding: '2px 5px 2px 4px', borderRadius: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', letterSpacing: '0.05em', backdropFilter: 'blur(4px)' }}>
                                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polygon points="12 2 2 7 12 12 22 7 12 2" />
                                                        <polyline points="2 17 12 22 22 17" />
                                                        <polyline points="2 12 12 17 22 12" />
                                                    </svg>
                                                    BUNDLE
                                                </div>
                                            )}
                                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '3px 6px', fontSize: 9, color: '#fff', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {title}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Auto load-more spinner — appears while the next page is fetching. */}
                        {loadingMore && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '14px 0 4px', fontSize: 11, color: '#78716c' }}>
                                <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(0,0,0,0.15)', borderTopColor: '#111827', borderRadius: '50%', animation: 'igSpin 0.7s linear infinite' }} />
                                Loading more…
                            </div>
                        )}

                        {!loadingMore && !hasMore && !loading && products.length > 0 && (
                            <div style={{ textAlign: 'center', padding: '12px 0 4px', fontSize: 10, color: '#a8a29e', fontStyle: 'italic' }}>
                                End of products
                            </div>
                        )}
                    </>
                ) : (
                    /* ── Giphy tab body ── */
                    <>
                        {giphyError && (
                            <div style={{ fontSize: 11, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                                {giphyError}
                            </div>
                        )}

                        {giphyLoading && giphyResults.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '28px 8px' }}>
                                <span style={{ display: 'inline-block', width: 18, height: 18, border: '2.5px solid rgba(0,0,0,0.15)', borderTopColor: '#111827', borderRadius: '50%', animation: 'igSpin 0.7s linear infinite' }} />
                                <div style={{ fontSize: 11, color: '#78716c', marginTop: 8, fontStyle: 'italic' }}>{giphyQuery.trim() ? 'Searching GIFs…' : 'Loading trending…'}</div>
                            </div>
                        ) : giphyResults.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '24px 8px' }}>
                                <div style={{ fontSize: 24, marginBottom: 6, opacity: 0.5 }}>🎬</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#44403c' }}>{giphyQuery.trim() ? 'No GIFs found' : 'No trending GIFs'}</div>
                                <div style={{ fontSize: 11, marginTop: 2, fontStyle: 'italic', color: '#78716c' }}>Try a different search.</div>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                {giphyResults.map((gif) => {
                                    const isAdding = addingGifId === gif.id;
                                    return (
                                        <div
                                            key={gif.id}
                                            onClick={() => handleGifClick(gif)}
                                            title={`${gif.title} — click to add to library`}
                                            style={{
                                                aspectRatio: '1', borderRadius: 8, position: 'relative', overflow: 'hidden',
                                                border: '1px solid rgba(214,211,209,0.7)', cursor: isAdding ? 'progress' : 'pointer',
                                                background: '#f5f5f4',
                                                boxShadow: '0 2px 4px -1px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.7)',
                                                opacity: isAdding ? 0.5 : 1,
                                                transition: 'transform 220ms ease, box-shadow 220ms ease, opacity 200ms ease',
                                            }}
                                            onMouseEnter={(e) => { if (!isAdding) e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                                        >
                                            <img src={gif.previewUrl || gif.url} alt={gif.title} draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                            {isAdding && (
                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)' }}>
                                                    <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(0,0,0,0.2)', borderTopColor: '#111827', borderRadius: '50%', animation: 'igSpin 0.7s linear infinite' }} />
                                                </div>
                                            )}
                                            <div style={{ position: 'absolute', top: 4, right: 4, fontSize: 8, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: 'rgba(0,0,0,0.55)', color: '#fff', letterSpacing: '0.05em' }}>GIF</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// --- PostCard ---
function PostCard({ post, media, isTop, stackSize, justDropped, onEdit, onDelete }) {
    const [offsetX, setOffsetX] = useState(0);
    const [swiping, setSwiping] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [hover, setHover] = useState(false);
    const swipeStart = useRef({ x: 0, moved: false });
    const offsetRef = useRef(0);
    useEffect(() => { offsetRef.current = offsetX; }, [offsetX]);
    const SWIPE_THRESHOLD = 80;

    const onPointerDown = (e) => {
        if (e.button != null && e.button !== 0) return;
        const cx = e.clientX ?? e.touches?.[0]?.clientX;
        if (cx == null) return;
        swipeStart.current = { x: cx, moved: false };
        setSwiping(true);
    };
    useEffect(() => {
        if (!swiping) return;
        const onMove = (e) => { const cx = e.clientX ?? e.touches?.[0]?.clientX; if (cx == null) return; const dx = cx - swipeStart.current.x; if (Math.abs(dx) > 3) swipeStart.current.moved = true; setOffsetX(dx); };
        const onUp = () => { const dx = offsetRef.current; setSwiping(false); if (Math.abs(dx) > SWIPE_THRESHOLD) { setRemoving(true); setOffsetX(dx > 0 ? 600 : -600); setTimeout(() => onDelete?.(), 240); } else { setOffsetX(0); } };
        window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [swiping, onDelete]);
    const handleClick = (e) => { if (swipeStart.current.moved) { e.stopPropagation(); return; } onEdit?.(); };

    if (!media) return null;
    const fade = Math.min(1, Math.abs(offsetX) / 160);
    const willDelete = Math.abs(offsetX) > SWIPE_THRESHOLD;
    const timeStr = formatHour(post.hour);

    return (
        <div
            onMouseDown={onPointerDown}
            onClick={handleClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            title="Click anywhere to edit · drag left/right to remove"
            style={{
                position: 'relative',
                filter: isTop ? 'drop-shadow(0 6px 8px rgba(0,0,0,0.12))' : 'drop-shadow(0 2px 3px rgba(0,0,0,0.08))',
                transform: `translateX(${offsetX}px) rotate(${offsetX * 0.05}deg)`,
                opacity: removing ? 0 : 1 - fade * 0.6,
                transition: swiping ? 'none' : removing ? 'transform 240ms cubic-bezier(.4,.1,.6,1), opacity 240ms ease' : 'transform 240ms cubic-bezier(.2,.9,.2,1.2), opacity 160ms ease',
                animation: justDropped ? 'igPaperFlutter 620ms cubic-bezier(.2,.7,.2,1) both' : undefined,
                transformOrigin: 'top center',
                cursor: 'pointer',
            }}
        >
            {!isTop && <div style={{ height: 13 }} />}
            <div style={{
                position: 'relative', height: 68, borderRadius: 8, overflow: 'hidden',
                border: `1px solid ${willDelete ? '#111827' : (hover ? 'rgba(17,24,39,0.45)' : 'rgba(17,24,39,0.22)')}`,
                boxShadow: hover
                    ? '0 6px 14px -4px rgba(0,0,0,0.22), inset 0 0.5px 0 rgba(255,255,255,0.6)'
                    : '0 2px 6px -2px rgba(0,0,0,0.14), inset 0 0.5px 0 rgba(255,255,255,0.6)',
                transition: 'box-shadow 160ms ease, border-color 160ms ease',
            }}>
                {(() => {
                    const fr = post.frameId && post.frameId !== 'none' ? POST_FRAMES.find((f) => f.id === post.frameId) : null;
                    // For framed posts, render the React overlay on the ORIGINAL
                    // product image so we don't paint a frame on top of an
                    // already-framed composite (which is the URL we ship to IG).
                    const src = media?.productImageUrl || media?.url;
                    return fr
                        ? <PostFrameRender frame={fr} productImage={src} productTitle={media?.title} bundle={media?.bundle} />
                        : <MediaThumb media={media} />;
                })()}

                {/* Time chip — always visible, high-contrast so it reads over any
                    image. Fixed top-left so stacked cards overlapping above
                    don't obscure it. */}
                <div style={{
                    position: 'absolute', top: 5, left: 5, zIndex: 2,
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: 'rgba(17,24,39,0.82)',
                    color: '#fff',
                    fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
                    backdropFilter: 'blur(3px)',
                    WebkitBackdropFilter: 'blur(3px)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                    pointerEvents: 'none',
                }}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                    {timeStr}
                </div>

                {/* Hover overlay → "Edit" affordance (hidden while swiping or removing) */}
                {hover && Math.abs(offsetX) < 20 && !removing && (
                    <div style={{
                        position: 'absolute', inset: 0, zIndex: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 4,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.55))',
                        color: '#fff', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
                        pointerEvents: 'none',
                        animation: 'postCardEdit 140ms ease-out both',
                    }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                        Edit
                        <style>{`@keyframes postCardEdit { from { opacity: 0; } to { opacity: 1; } }`}</style>
                    </div>
                )}

                {Math.abs(offsetX) > 20 && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `rgba(244,63,94,${Math.min(0.85, fade * 0.8)})`, pointerEvents: 'none' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                    </div>
                )}
            </div>
        </div>
    );
}

// --- FeedView ---
function FeedView({ weekStart, scheduled, mediaMap, dragOverCell, onCellDragOver, onCellDrop, setDragOverCell, isDragActive, justDroppedId, onEdit, onDelete }) {
    const todayOffset = (() => {
        const t = new Date().toDateString();
        for (let i = 0; i < 7; i++) if (addDays(weekStart, i).toDateString() === t) return i;
        return -1;
    })();
    const todayBorders = (i) => i === todayOffset
        ? { borderLeft: '1px solid #111827', borderRight: '1px solid #111827' }
        : { borderLeft: '1px solid #f5f5f4' };

    // Live "now" indicator — re-renders every minute so the red line follows real time.
    const [nowTick, setNowTick] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNowTick(new Date()), 60000);
        return () => clearInterval(id);
    }, []);
    const nowFloat = nowTick.getHours() + nowTick.getMinutes() / 60;
    // The grid spans the full 24-hour day, so the indicator is visible whenever
    // today is in the visible 7-day window.
    const nowVisible = todayOffset >= 0;

    return (
        <>
            {/* Day headers — sticky inside the calendar scroll container so
                they stay pinned as the hour grid scrolls. Solid background
                so rows sliding underneath don't bleed through. */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '88px repeat(7, minmax(0, 1fr))',
                background: '#ffffff',
                position: 'sticky',
                top: 0,
                zIndex: 3,
                boxShadow: '0 1px 0 #e7e5e4, 0 6px 12px -10px rgba(0,0,0,0.08)',
            }}>
                <div />
                {Array.from({ length: 7 }).map((_, i) => {
                    const d = addDays(weekStart, i);
                    const isToday = d.toDateString() === new Date().toDateString();
                    return (
                        <div key={i} style={{
                            padding: '12px 14px',
                            ...todayBorders(i),
                            borderTop: isToday ? '1px solid #111827' : undefined,
                            borderBottom: isToday ? '1px solid #111827' : '1px solid transparent',
                            fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em',
                            color: isToday ? '#111827' : '#78716c',
                            display: 'flex', alignItems: 'center', gap: 8,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                minWidth: 24, height: 24, padding: '0 6px', borderRadius: 6,
                                fontSize: 12, fontWeight: 600,
                                color: isToday ? '#fff' : '#44403c',
                                background: isToday ? '#111827' : 'transparent',
                            }}>{d.getDate()}</span>
                            <span style={{ fontWeight: isToday ? 600 : 500 }}>
                                {DAY_LABELS[d.getDay()]}
                            </span>
                        </div>
                    );
                })}
            </div>
            {/* Hour grid — no own scroll, page scrolls */}
            <div onDragLeave={() => setDragOverCell(null)}>
                {HOURS.map((h) => {
                    const isHalf = h % 1 !== 0;
                    const showNowLine = nowVisible && nowFloat >= h && nowFloat < h + 0.5;
                    return (
                    <div key={h} style={{
                        display: 'grid',
                        gridTemplateColumns: '88px repeat(7, minmax(0, 1fr))',
                        borderTop: isHalf ? '1px dashed #f5f5f4' : '1px solid #f5f5f4',
                        position: 'relative',
                    }}>
                        {showNowLine && (
                            <div aria-hidden style={{
                                position: 'absolute',
                                top: `${((nowFloat - h) / 0.5) * 100}%`,
                                left: 0, right: 0,
                                height: 0,
                                borderTop: '1px solid #111827',
                                zIndex: 4,
                                pointerEvents: 'none',
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    left: 0, top: '-4px', // half of triangle height so tip sits on line
                                    width: 0, height: 0,
                                    borderTop: '4px solid transparent',
                                    borderBottom: '4px solid transparent',
                                    borderLeft: '6px solid #111827',
                                }} />
                            </div>
                        )}
                        <div style={{ padding: '8px 12px', fontSize: 11, color: '#a8a29e', fontWeight: 500 }}>{formatHour(h)}</div>
                        {Array.from({ length: 7 }).map((_, dayOffset) => {
                            const cellKey = `${dayOffset}-${h}`;
                            // Match posts whose precise hour falls within this 30-min slot
                            // (e.g. a post at 14.0833 = 2:05 PM lands in the 14.0 slot).
                            const postsHere = scheduled.filter((p) => p.dayOffset === dayOffset && p.hour >= h && p.hour < h + 0.5);
                            const isHot = dragOverCell === cellKey;
                            return (
                                <div key={dayOffset} onDragOver={(e) => onCellDragOver(e, dayOffset, h)} onDrop={(e) => onCellDrop(e, dayOffset, h)} style={{
                                    position: 'relative', ...todayBorders(dayOffset), padding: 6, minHeight: 48, transition: 'all 0.15s',
                                    background: isHot ? 'linear-gradient(135deg, rgba(251,191,36,0.10), rgba(251,113,133,0.10))' : isDragActive ? 'rgba(254,243,199,0.3)' : '#ffffff',
                                    boxShadow: isHot ? 'inset 0 0 0 1.5px rgba(251,113,133,0.45), inset 0 0 24px rgba(251,113,133,0.08)' : 'none',
                                }}>
                                    {postsHere.map((p, idx) => (
                                        <div key={p.id} style={{
                                            position: 'relative', marginTop: idx > 0 ? -52 : 0,
                                            transform: postsHere.length > 1 ? `rotate(${(idx - (postsHere.length - 1) / 2) * 2.2}deg) translateX(${(idx - (postsHere.length - 1) / 2) * 2}px)` : undefined,
                                            zIndex: idx + 1, transition: 'transform 220ms cubic-bezier(.2,.9,.2,1.2)',
                                        }}>
                                            <PostCard post={p} media={mediaMap.get(p.mediaId) || p.media} isTop={idx === postsHere.length - 1} stackSize={postsHere.length}
                                                justDropped={justDroppedId === p.id} onEdit={() => onEdit(p)} onDelete={() => onDelete(p.id)} />
                                        </div>
                                    ))}
                                    {postsHere.length > 1 && (
                                        <div style={{ position: 'absolute', top: 4, right: 4, zIndex: 20, fontSize: 9, fontWeight: 600, color: '#fff', background: 'rgba(28,25,23,0.8)', borderRadius: 99, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {postsHere.length}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    );
                })}
            </div>
        </>
    );
}

// --- StoryPhone ---
// Renders a 9:16 phone tile for a single day. If a story is set we render
// the selected story frame applied to the chosen product image, plus the
// usual IG progress bars + handle so it reads as a real story. If empty we
// show the drop affordance.
function StoryPhone({ slides = [], isHot, dayOffset, onDragOver, onDrop, onPickSlide, onAddSlide, accountHandle = 'your.handle', accountAvatarUrl = '' }) {
    const [activeIdx, setActiveIdx] = useState(0);
    const hasStory = slides.length > 0;
    // Clamp the active index whenever slides shrink (e.g. user deletes one).
    useEffect(() => {
        if (activeIdx > Math.max(0, slides.length - 1)) {
            setActiveIdx(Math.max(0, slides.length - 1));
        }
    }, [slides.length, activeIdx]);
    const safeIdx = Math.min(activeIdx, Math.max(0, slides.length - 1));
    const story = slides[safeIdx] || null;
    const frame = hasStory && story?.frameId ? STORY_FRAMES.find((f) => f.id === story.frameId) : null;
    const goPrev = (e) => { e.stopPropagation(); setActiveIdx((i) => Math.max(0, i - 1)); };
    const goNext = (e) => { e.stopPropagation(); setActiveIdx((i) => Math.min(slides.length - 1, i + 1)); };
    const handleAdd = (e) => { e.stopPropagation(); onAddSlide?.(dayOffset); };
    const handleClick = () => {
        if (hasStory && story) onPickSlide?.(story);
        else onAddSlide?.(dayOffset);
    };
    return (
        <div onDragOver={onDragOver} onDrop={onDrop} onClick={handleClick} style={{
            width: '100%', aspectRatio: '9 / 16', borderRadius: 20, position: 'relative', overflow: 'hidden', cursor: 'pointer',
            background: hasStory ? '#000' : 'linear-gradient(145deg, rgba(255,255,255,0.7), rgba(250,245,238,0.8))',
            border: hasStory ? '2px solid rgba(255,255,255,0.4)' : '2px dashed rgba(120,113,108,0.35)',
            boxShadow: hasStory ? '0 20px 40px -15px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.15)' : '0 6px 16px -8px rgba(0,0,0,0.1)',
            transform: isHot ? 'scale(1.03)' : 'none', outline: isHot ? '4px solid #111827' : 'none',
            transition: 'transform 200ms ease, outline 200ms ease',
        }} title={hasStory ? 'Tap slide to edit · arrows to switch · + to add' : 'Drop product or tap to build'}>
            {hasStory ? (
                <>
                    <StoryFrameRender frame={frame || STORY_FRAMES[0]} fields={story.fields} productImage={story.media?.productImageUrl || story.media?.url} productTitle={story.media?.title} />
                    {/* Progress bars — one segment per slide; active is partially filled, prior ones fully */}
                    <div style={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', gap: 3, zIndex: 10, pointerEvents: 'none' }}>
                        {slides.map((_, i) => (
                            <div key={i} style={{ flex: 1, height: 2, borderRadius: 99, background: 'rgba(255,255,255,0.35)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', background: '#fff', width: i < safeIdx ? '100%' : i === safeIdx ? '60%' : '0%' }} />
                            </div>
                        ))}
                    </div>
                    {/* Prev arrow */}
                    {safeIdx > 0 && (
                        <button onClick={goPrev} title="Previous slide" style={{
                            position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                            width: 30, height: 30, borderRadius: 99, border: 'none',
                            background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11,
                        }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
                        </button>
                    )}
                    {/* Next arrow OR + Add (when on the last slide) */}
                    {safeIdx < slides.length - 1 ? (
                        <button onClick={goNext} title="Next slide" style={{
                            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                            width: 30, height: 30, borderRadius: 99, border: 'none',
                            background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11,
                        }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                    ) : (
                        <button onClick={handleAdd} title="Add another slide" style={{
                            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                            width: 30, height: 30, borderRadius: 99, border: '1px solid rgba(255,255,255,0.55)',
                            background: 'rgba(255,255,255,0.18)', color: '#fff', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11,
                            backdropFilter: 'blur(3px)',
                        }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        </button>
                    )}
                    {/* Slide counter (only when 2+) */}
                    {slides.length > 1 && (
                        <div style={{
                            position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
                            zIndex: 10, pointerEvents: 'none',
                            fontSize: 10, fontWeight: 700, color: '#fff',
                            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
                            borderRadius: 99, padding: '3px 9px',
                        }}>{safeIdx + 1} / {slides.length}</div>
                    )}
                    {/* Handle */}
                    <div style={{ position: 'absolute', top: 20, left: 12, display: 'flex', alignItems: 'center', gap: 6, zIndex: 10, pointerEvents: 'none' }}>
                        <div style={{ width: 22, height: 22, borderRadius: 99, background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, overflow: 'hidden' }}>
                            {accountAvatarUrl ? <img src={accountAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '📷'}
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>{accountHandle}</span>
                    </div>
                </>
            ) : (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, textAlign: 'center' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 99, background: '#f5f5f4', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a8a29e" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    </div>
                    <div style={{ fontSize: 11, color: '#78716c', fontWeight: 500, lineHeight: 1.5 }}>Drag product<br />or tap to build</div>
                </div>
            )}
        </div>
    );
}

// --- StoriesView ---
// Drop a product on a day → opens the story composer pre-filled with that
// day + product. Click an existing phone → opens the composer to edit.
function StoriesView({ weekStart, stories, draggingMedia, onDropStory, onPickStory, onAddStory, accountHandle, accountAvatarUrl }) {
    const [dragOverDay, setDragOverDay] = useState(null);
    const handleDayDragOver = (e, dayOffset) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOverDay(dayOffset); };
    const handleDayDrop = (e, dayOffset) => {
        e.preventDefault();
        if (!draggingMedia) { setDragOverDay(null); return; }
        onDropStory?.(dayOffset, draggingMedia);
        setDragOverDay(null);
    };

    return (
        <div style={{ padding: '24px 24px 16px' }} onDragLeave={() => setDragOverDay(null)}>
            <div className="ig-scroll" style={{ overflowX: 'auto' }}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'start', paddingBottom: 8, paddingLeft: 8, paddingRight: 8, width: 'max-content' }}>
                    {Array.from({ length: 7 }).map((_, dayOffset) => {
                        const d = addDays(weekStart, dayOffset);
                        const isToday = d.toDateString() === new Date().toDateString();
                        const slides = stories[dayOffset] || [];
                        const hasStory = slides.length > 0;
                        const isHot = dragOverDay === dayOffset;
                        return (
                            <div key={dayOffset} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 200 }}>
                                <div style={{ marginBottom: 8, textAlign: 'center' }}>
                                    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: isToday ? '#111827' : '#78716c', fontWeight: isToday ? 700 : 400 }}>
                                        {DAY_LABELS[d.getDay()]}
                                        {isToday && <span style={{ marginLeft: 4, color: '#dc2626' }}>•</span>}
                                    </div>
                                    <div style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        marginTop: 2, minWidth: 24, height: 24, padding: '0 7px',
                                        borderRadius: 99,
                                        fontSize: 13, fontWeight: 700,
                                        color: isToday ? '#fff' : '#292524',
                                        background: isToday ? '#111827' : 'transparent',
                                        boxShadow: isToday ? '0 4px 12px -4px rgba(0,0,0,0.35)' : 'none',
                                    }}>{d.getDate()}</div>
                                </div>
                                <StoryPhone slides={slides} isHot={isHot} dayOffset={dayOffset}
                                    accountHandle={accountHandle} accountAvatarUrl={accountAvatarUrl}
                                    onDragOver={(e) => handleDayDragOver(e, dayOffset)} onDrop={(e) => handleDayDrop(e, dayOffset)}
                                    onPickSlide={onPickStory} onAddSlide={onAddStory} />
                                <div style={{ marginTop: 12, fontSize: 10, textAlign: 'center', color: '#78716c' }}>
                                    {hasStory ? (
                                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><path d="M12 3l1.5 4.5H18l-3.5 2.7 1.3 4.3L12 11.8l-3.8 2.7 1.3-4.3L6 7.5h4.5z" /></svg>
                                            {slides.length} slide{slides.length !== 1 ? 's' : ''}
                                        </span>
                                    ) : (<span style={{ color: '#a8a29e' }}>No story</span>)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div style={{ textAlign: 'center', fontSize: 11, color: '#a8a29e', fontStyle: 'italic', marginTop: 8 }}>
                Drag a product onto a day, or tap a phone to open the story composer
            </div>
        </div>
    );
}

/**
 * ThemedSelect — fully custom dropdown (NOT a native <select>). Takes the
 * same `<option value>label</option>` children shape as a native select so
 * call sites don't change, but renders its own themed trigger + popover
 * menu, with keyboard nav, click-outside, and scale-fade animations.
 */
function ThemedSelect({ icon, label, value, onChange, children }) {
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const rootRef = useRef(null);
    const menuRef = useRef(null);

    // Normalize children (<option>) into {value, label, disabled} objects.
    const options = React.Children.toArray(children)
        .filter((c) => c && c.props && c.props.value !== undefined)
        .map((c) => ({ value: c.props.value, label: c.props.children, disabled: !!c.props.disabled }));

    const currentIdx = Math.max(0, options.findIndex((o) => String(o.value) === String(value)));
    const currentLabel = options[currentIdx]?.label ?? '';

    // Sync active highlight to the selected option (or first enabled option) whenever the menu opens.
    useEffect(() => {
        if (!open) return;
        if (!options[currentIdx]?.disabled) {
            setActiveIdx(currentIdx);
        } else {
            const firstEnabled = options.findIndex((o) => !o.disabled);
            setActiveIdx(firstEnabled >= 0 ? firstEnabled : 0);
        }
    }, [open, currentIdx]);

    // Click outside → close.
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    // Scroll the active option into view when it changes.
    useEffect(() => {
        if (!open || !menuRef.current) return;
        const el = menuRef.current.querySelector(`[data-idx="${activeIdx}"]`);
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIdx, open]);

    const pick = (idx) => {
        const opt = options[idx];
        if (!opt || opt.disabled) return;
        onChange?.(opt.value);
        setOpen(false);
    };

    // Arrow-key nav skips disabled options. Returns the next enabled index in
    // `dir` (1 = down, -1 = up), falling back to current if none found.
    const moveActive = (from, dir) => {
        let i = from;
        for (let step = 0; step < options.length; step++) {
            i += dir;
            if (i < 0 || i >= options.length) return from;
            if (!options[i].disabled) return i;
        }
        return from;
    };

    const handleKey = (e) => {
        if (!open) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setOpen(true);
            }
            return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => moveActive(i, 1)); return; }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => moveActive(i, -1)); return; }
        if (e.key === 'Home') {
            e.preventDefault();
            const first = options.findIndex((o) => !o.disabled);
            if (first >= 0) setActiveIdx(first);
            return;
        }
        if (e.key === 'End') {
            e.preventDefault();
            for (let i = options.length - 1; i >= 0; i--) {
                if (!options[i].disabled) { setActiveIdx(i); break; }
            }
            return;
        }
        if (e.key === 'Enter')     { e.preventDefault(); pick(activeIdx); return; }
    };

    return (
        <div ref={rootRef} style={{ position: 'relative' }}>
            <button
                type="button"
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                onKeyDown={handleKey}
                style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 36px 8px 12px',
                    borderRadius: 10,
                    background: open ? '#fff' : '#fafaf9',
                    border: `1px solid ${open ? '#1f2937' : '#e7e5e4'}`,
                    boxShadow: open ? '0 0 0 3px rgba(31,41,55,0.08)' : 'none',
                    transition: 'border-color 150ms ease, box-shadow 150ms ease, background 150ms ease',
                    cursor: 'pointer',
                    font: 'inherit',
                    textAlign: 'left',
                    position: 'relative',
                }}
                onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = '#f5f5f4'; }}
                onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = '#fafaf9'; }}
            >
                {icon && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: open ? '#1f2937' : '#a8a29e', flexShrink: 0, transition: 'color 150ms ease' }}>
                        {icon}
                    </span>
                )}
                <span style={{ flex: 1, minWidth: 0 }}>
                    {label && (
                        <span style={{ display: 'block', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#a8a29e', lineHeight: 1, marginBottom: 2 }}>
                            {label}
                        </span>
                    )}
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                        {currentLabel}
                    </span>
                </span>
                <svg
                    width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke={open ? '#1f2937' : '#a8a29e'}
                    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                    style={{
                        position: 'absolute', right: 12, top: '50%',
                        transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
                        pointerEvents: 'none', transition: 'stroke 150ms ease, transform 180ms ease',
                    }}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <div
                    ref={menuRef}
                    role="listbox"
                    className="ig-scroll"
                    style={{
                        position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                        zIndex: 60,
                        background: '#fff',
                        border: '1px solid #e7e5e4',
                        borderRadius: 10,
                        boxShadow: '0 14px 40px -12px rgba(0,0,0,0.18), 0 4px 10px -4px rgba(0,0,0,0.06)',
                        maxHeight: 260,
                        overflowY: 'auto',
                        padding: 4,
                        animation: 'themedSelectOpen 140ms cubic-bezier(.2,.9,.3,1.1) both',
                        transformOrigin: 'top center',
                    }}
                >
                    <style>{`
                        @keyframes themedSelectOpen {
                            from { opacity: 0; transform: scale(0.97) translateY(-4px); }
                            to   { opacity: 1; transform: scale(1) translateY(0); }
                        }
                    `}</style>
                    {options.map((o, i) => {
                        const selected = String(o.value) === String(value);
                        const active = i === activeIdx && !o.disabled;
                        return (
                            <div
                                key={`${o.value}-${i}`}
                                role="option"
                                aria-selected={selected}
                                aria-disabled={o.disabled || undefined}
                                data-idx={i}
                                onMouseEnter={() => { if (!o.disabled) setActiveIdx(i); }}
                                onClick={() => pick(i)}
                                title={o.disabled ? 'Past time — pick a slot from now onwards' : undefined}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '8px 10px',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    fontWeight: selected ? 600 : 500,
                                    color: o.disabled ? '#c7c2bd' : (selected ? '#111827' : '#44403c'),
                                    background: active ? '#f5f5f4' : 'transparent',
                                    cursor: o.disabled ? 'not-allowed' : 'pointer',
                                    transition: 'background 120ms ease',
                                }}
                            >
                                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {o.label}
                                </span>
                                {selected && !o.disabled && (
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Custom 24-hour time picker styled to match ThemedSelect — but minute-precise.
// `value` is a fractional hour (e.g. 14.25 = 2:15 PM). `minHour` (optional) is
// the earliest valid fractional hour: any combination resolving below it is
// disabled and visually greyed out.
function ThemedTimeSelect({ icon, label, value, onChange, minHour }) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const hourColRef = useRef(null);
    const minuteColRef = useRef(null);

    const [h24, minute] = hourToHM(value);
    const ampm = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;

    const isPast = (testH24, testMin) =>
        minHour !== undefined && testH24 + testMin / 60 < minHour;

    const h24From = (testH12, testAmpm) => {
        const base = testH12 % 12;
        return testAmpm === 'PM' ? base + 12 : base;
    };

    // Hour disabled if even minute=59 can't beat minHour for the current AM/PM.
    const isHourDisabled = (testH12) => isPast(h24From(testH12, ampm), 59);
    const isMinuteDisabled = (m) => isPast(h24, m);
    const isPeriodDisabled = (p) => isPast(h24From(h12, p), 59);

    // When any field changes, bump the minute up if the new combination would
    // land before minHour (e.g. switching from 11 PM → 5 PM on today at 5:30).
    const commit = (newH24, m) => {
        if (minHour !== undefined && newH24 + m / 60 < minHour) {
            const requiredMin = Math.ceil((minHour - newH24) * 60);
            m = Math.min(59, Math.max(0, requiredMin));
        }
        onChange?.(newH24 + m / 60);
    };

    const updateHour = (newH12) => {
        if (isHourDisabled(newH12)) return;
        commit(h24From(newH12, ampm), minute);
    };
    const updateMinute = (m) => {
        if (isMinuteDisabled(m)) return;
        onChange?.(h24 + m / 60);
    };
    const updatePeriod = (p) => {
        if (isPeriodDisabled(p)) return;
        commit(h24From(h12, p), minute);
    };

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    // Center the selected hour/minute in their scroll columns when opening.
    useEffect(() => {
        if (!open) return;
        const center = (col) => {
            const sel = col?.querySelector('[data-selected="true"]');
            if (sel?.scrollIntoView) sel.scrollIntoView({ block: 'center' });
        };
        center(hourColRef.current);
        center(minuteColRef.current);
    }, [open]);

    const triggerLabel = `${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ampm}`;

    const colStyle = {
        maxHeight: 200,
        overflowY: 'auto',
        padding: 4,
        borderRadius: 8,
        background: '#fafaf9',
    };
    const optBase = {
        padding: '6px 8px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 500,
        textAlign: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background 120ms ease, color 120ms ease',
    };
    const renderOption = (key, text, selected, disabled, onClick) => (
        <div
            key={key}
            data-selected={selected || undefined}
            onClick={disabled ? undefined : onClick}
            title={disabled ? 'Past time — pick a slot from now onwards' : undefined}
            style={{
                ...optBase,
                color: disabled ? '#c7c2bd' : selected ? '#fff' : '#44403c',
                background: selected ? '#1f2937' : 'transparent',
                fontWeight: selected ? 600 : 500,
                cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => { if (!disabled && !selected) e.currentTarget.style.background = '#f5f5f4'; }}
            onMouseLeave={(e) => { if (!disabled && !selected) e.currentTarget.style.background = 'transparent'; }}
        >
            {text}
        </div>
    );

    return (
        <div ref={rootRef} style={{ position: 'relative' }}>
            <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 36px 8px 12px',
                    borderRadius: 10,
                    background: open ? '#fff' : '#fafaf9',
                    border: `1px solid ${open ? '#1f2937' : '#e7e5e4'}`,
                    boxShadow: open ? '0 0 0 3px rgba(31,41,55,0.08)' : 'none',
                    transition: 'border-color 150ms ease, box-shadow 150ms ease, background 150ms ease',
                    cursor: 'pointer',
                    font: 'inherit',
                    textAlign: 'left',
                    position: 'relative',
                }}
                onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = '#f5f5f4'; }}
                onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = '#fafaf9'; }}
            >
                {icon && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: open ? '#1f2937' : '#a8a29e', flexShrink: 0, transition: 'color 150ms ease' }}>
                        {icon}
                    </span>
                )}
                <span style={{ flex: 1, minWidth: 0 }}>
                    {label && (
                        <span style={{ display: 'block', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#a8a29e', lineHeight: 1, marginBottom: 2 }}>
                            {label}
                        </span>
                    )}
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                        {triggerLabel}
                    </span>
                </span>
                <svg
                    width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke={open ? '#1f2937' : '#a8a29e'}
                    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                    style={{
                        position: 'absolute', right: 12, top: '50%',
                        transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
                        pointerEvents: 'none', transition: 'stroke 150ms ease, transform 180ms ease',
                    }}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <div
                    role="dialog"
                    className="ig-scroll"
                    style={{
                        position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                        zIndex: 60,
                        minWidth: 240,
                        background: '#fff',
                        border: '1px solid #e7e5e4',
                        borderRadius: 10,
                        boxShadow: '0 14px 40px -12px rgba(0,0,0,0.18), 0 4px 10px -4px rgba(0,0,0,0.06)',
                        padding: 8,
                        animation: 'themedSelectOpen 140ms cubic-bezier(.2,.9,.3,1.1) both',
                        transformOrigin: 'top right',
                    }}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.85fr', gap: 6 }}>
                        <div>
                            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#a8a29e', textAlign: 'center', marginBottom: 4 }}>Hour</div>
                            <div ref={hourColRef} style={colStyle}>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((hh) =>
                                    renderOption(
                                        `h-${hh}`,
                                        String(hh).padStart(2, '0'),
                                        hh === h12,
                                        isHourDisabled(hh),
                                        () => updateHour(hh),
                                    ),
                                )}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#a8a29e', textAlign: 'center', marginBottom: 4 }}>Min</div>
                            <div ref={minuteColRef} style={colStyle}>
                                {Array.from({ length: 60 }, (_, i) => i).map((m) =>
                                    renderOption(
                                        `m-${m}`,
                                        String(m).padStart(2, '0'),
                                        m === minute,
                                        isMinuteDisabled(m),
                                        () => updateMinute(m),
                                    ),
                                )}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#a8a29e', textAlign: 'center', marginBottom: 4 }}>Period</div>
                            <div style={colStyle}>
                                {['AM', 'PM'].map((p) =>
                                    renderOption(
                                        `p-${p}`,
                                        p,
                                        p === ampm,
                                        isPeriodDisabled(p),
                                        () => updatePeriod(p),
                                    ),
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Post Frame Templates (1:1 for IG feed posts) ---
// Each frame overlays decoration + offer text on top of the product image.
// Defaults are shown in the gallery and the live preview.
const POST_FRAMES = [
    { id: 'none', name: 'No Frame', style: 'none', accent: '#1f2937', secondary: '#fff', defaults: {} },
    { id: 'sale-burst', name: 'Sale Burst', style: 'burst', accent: '#E11D2E', secondary: '#fff', defaults: { discountPct: 30, urgency: 'LIMITED TIME' } },
    { id: 'discount-badge', name: 'Discount Badge', style: 'badge', accent: '#D4A24A', secondary: '#0B0F1A', defaults: { discountPct: 25, urgency: 'TODAY ONLY' } },
    { id: 'bogo-banner', name: 'BOGO', style: 'bogo', accent: '#0BAA7A', secondary: '#0F3D2E', defaults: { buyQty: 1, getQty: 1, urgency: 'Free shipping' } },
    { id: 'spotlight', name: 'Spotlight', style: 'spotlight', accent: '#1F2937', secondary: '#FACC15', defaults: { headline: 'New Arrival', cta: 'SHOP NOW' } },
    { id: 'coming-soon', name: 'Coming Soon', style: 'soon', accent: '#EC4899', secondary: '#1E3A8A', defaults: { headline: 'COMING SOON', urgency: 'Stay tuned' } },
];

function PostFrameRender({ frame, fields = {}, productImage, productTitle = '', bundle = null }) {
    const f = { ...(frame?.defaults || {}), ...fields };
    const isBundle = !!(bundle && Array.isArray(bundle.products) && bundle.products.length > 0);
    const ImageBase = isBundle
        ? <BundleCollageFill products={bundle.products} template={bundle.collageTemplate} />
        : productImage
            ? <img src={productImage} alt={productTitle} draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ position: 'absolute', inset: 0, background: '#f5f5f4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a8a29e', fontSize: 32 }}>🛍️</div>;

    if (!frame || frame.style === 'none') {
        return <div style={{ width: '100%', height: '100%', position: 'relative', background: '#f5f5f4' }}>{ImageBase}</div>;
    }

    if (frame.style === 'burst') {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#fff' }}>
                {ImageBase}
                <div style={{
                    position: 'absolute', top: '8%', left: '-22%', width: '80%',
                    transform: 'rotate(-38deg)', transformOrigin: 'center',
                    background: frame.accent, color: frame.secondary,
                    padding: '5% 0', fontSize: 'min(15px, 3.6vw)', fontWeight: 800,
                    letterSpacing: '0.16em', textAlign: 'center',
                    boxShadow: '0 4px 10px -4px rgba(0,0,0,0.4)',
                }}>
                    {f.discountPct}% OFF
                </div>
                {f.urgency && (
                    <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: 'rgba(0,0,0,0.75)', color: '#fff',
                        padding: '3.5% 4%', fontSize: 'min(11px, 2.6vw)', fontWeight: 700,
                        letterSpacing: '0.18em', textAlign: 'center', textTransform: 'uppercase',
                    }}>
                        {f.urgency}
                    </div>
                )}
            </div>
        );
    }

    if (frame.style === 'badge') {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#fff' }}>
                {ImageBase}
                <div style={{
                    position: 'absolute', top: '6%', right: '6%',
                    width: '32%', aspectRatio: '1', borderRadius: '50%',
                    background: frame.accent, color: frame.secondary,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 6px 16px -4px rgba(0,0,0,0.35)',
                    border: '3px solid #fff',
                    transform: 'rotate(-12deg)',
                }}>
                    <div style={{ fontSize: 'min(28px, 7.5vw)', fontWeight: 900, lineHeight: 1 }}>{f.discountPct}%</div>
                    <div style={{ fontSize: 'min(10px, 2.6vw)', fontWeight: 700, letterSpacing: '0.18em', marginTop: 1 }}>OFF</div>
                </div>
                {f.urgency && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0,
                        background: frame.secondary, color: frame.accent,
                        padding: '2.5% 4%', fontSize: 'min(10px, 2.6vw)', fontWeight: 700,
                        letterSpacing: '0.22em', textAlign: 'center', textTransform: 'uppercase',
                    }}>
                        {f.urgency}
                    </div>
                )}
            </div>
        );
    }

    if (frame.style === 'bogo') {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#fff' }}>
                {ImageBase}
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0,
                    background: frame.accent, color: '#fff',
                    padding: '4% 4%', fontSize: 'min(15px, 3.8vw)', fontWeight: 800,
                    letterSpacing: '0.08em', textAlign: 'center', textTransform: 'uppercase',
                }}>
                    BUY {f.buyQty} GET {f.getQty} FREE
                </div>
                {f.urgency && (
                    <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: frame.secondary, color: '#fff',
                        padding: '3% 4%', fontSize: 'min(11px, 2.8vw)', fontWeight: 600,
                        letterSpacing: '0.14em', textAlign: 'center',
                    }}>
                        {f.urgency}
                    </div>
                )}
            </div>
        );
    }

    if (frame.style === 'spotlight') {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#fff' }}>
                {ImageBase}
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '9% 5% 5%',
                    background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.88))',
                    color: '#fff', display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                    <div style={{ fontSize: 'min(16px, 4vw)', fontWeight: 700, letterSpacing: '-0.01em' }}>{f.headline}</div>
                    <div style={{
                        alignSelf: 'flex-start',
                        background: frame.secondary, color: frame.accent,
                        padding: '2% 5%', fontSize: 'min(11px, 2.8vw)', fontWeight: 800,
                        letterSpacing: '0.16em', textTransform: 'uppercase',
                        borderRadius: 99,
                    }}>
                        {f.cta} →
                    </div>
                </div>
            </div>
        );
    }

    if (frame.style === 'soon') {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#fff' }}>
                {ImageBase}
                <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,0.4)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    color: '#fff',
                }}>
                    <div style={{
                        background: frame.accent, color: '#fff',
                        padding: '4% 7%', fontSize: 'min(18px, 4.6vw)', fontWeight: 900,
                        letterSpacing: '0.18em', textAlign: 'center', textTransform: 'uppercase',
                        boxShadow: '0 8px 20px -4px rgba(0,0,0,0.4)',
                        transform: 'rotate(-2deg)',
                    }}>
                        {f.headline}
                    </div>
                    {f.urgency && (
                        <div style={{
                            marginTop: '4%', fontSize: 'min(11px, 2.8vw)', fontWeight: 600, letterSpacing: '0.22em',
                            textTransform: 'uppercase',
                        }}>
                            {f.urgency}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return <div style={{ width: '100%', height: '100%', position: 'relative' }}>{ImageBase}</div>;
}

// ---------------------------------------------------------------------------
// Story Frame Templates (9:16 vertical, for Instagram Stories)
// ---------------------------------------------------------------------------
const STORY_FRAMES = [
    { id: 'none', name: 'No Frame', style: 'none', accent: '#1f2937', secondary: '#fff', defaults: {} },
    { id: 'splash', name: 'Sale Splash', style: 'splash', accent: '#E11D2E', secondary: '#0F2A57', defaults: { discountPct: 30, urgency: '24 HOURS ONLY', cta: 'SHOP NOW' } },
    { id: 'spotlight-dark', name: 'Midnight', style: 'midnight', accent: '#D4A24A', secondary: '#0B0F1A', defaults: { discountPct: 15, headline: 'Members Only', urgency: 'Tap to shop', cta: 'SHOP NOW' } },
    { id: 'story-bogo', name: 'BOGO', style: 'storybogo', accent: '#0BAA7A', secondary: '#0F3D2E', defaults: { buyQty: 1, getQty: 1, urgency: 'Wellness drop', cta: 'SHOP NOW' } },
    { id: 'story-soon', name: 'Coming Soon', style: 'storysoon', accent: '#EC4899', secondary: '#1E3A8A', defaults: { headline: 'COMING SOON', urgency: 'Stay tuned', cta: 'SHOP NOW' } },
];

function StoryFrameRender({ frame, fields = {}, productImage, productTitle = '' }) {
    const f = { ...(frame?.defaults || {}), ...fields };
    const ImageBase = productImage
        ? <img src={productImage} alt={productTitle} draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        : <div style={{ position: 'absolute', inset: 0, background: '#f5f5f4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a8a29e', fontSize: 32 }}>🛍️</div>;
    const accent = frame?.accent || '#1f2937';
    const secondary = frame?.secondary || '#fff';

    if (!frame || frame.style === 'none') {
        return <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>{ImageBase}</div>;
    }

    const ShopNowPill = (
        <div style={{
            position: 'absolute', left: '50%', bottom: '8%', transform: 'translateX(-50%)',
            background: '#fff', borderRadius: 99, padding: '7px 14px',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            boxShadow: '0 4px 12px -2px rgba(0,0,0,0.3)', minWidth: '55%', justifyContent: 'center',
        }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#0f172a', letterSpacing: '0.08em' }}>{(f.cta || 'SHOP NOW').toUpperCase()}</span>
            <span style={{ fontSize: 10, color: '#0f172a' }}>→</span>
        </div>
    );

    if (frame.style === 'splash') {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#F2F2F2' }}>
                {ImageBase}
                {/* Big diagonal red splash top-left */}
                <div style={{
                    position: 'absolute', top: '-8%', left: '-15%', width: '85%', height: '50%',
                    background: accent,
                    borderBottomRightRadius: '60% 40%',
                    boxShadow: '0 10px 30px -8px rgba(0,0,0,0.35)',
                }} />
                <div style={{
                    position: 'absolute', top: '6%', left: '8%', right: '38%',
                    color: '#fff', fontWeight: 900,
                    fontSize: 'min(34px, 8vw)', lineHeight: 1, letterSpacing: '-0.01em',
                    textShadow: '2px 2px 0 rgba(0,0,0,0.15)',
                }}>
                    {f.discountPct}%<br />OFF
                </div>
                {f.urgency && (
                    <div style={{
                        position: 'absolute', left: '50%', bottom: '20%', transform: 'translateX(-50%)',
                        background: '#fff', color: secondary,
                        padding: '5px 12px', borderRadius: 99,
                        fontSize: 'min(11px, 2.6vw)', fontWeight: 800, letterSpacing: '0.18em',
                        textTransform: 'uppercase', whiteSpace: 'nowrap',
                        boxShadow: '0 4px 10px -4px rgba(0,0,0,0.3)',
                    }}>{f.urgency}</div>
                )}
                {ShopNowPill}
            </div>
        );
    }

    if (frame.style === 'midnight') {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: secondary }}>
                {ImageBase}
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(11,15,26,0.55), rgba(11,15,26,0.85))' }} />
                <div style={{ position: 'absolute', inset: '6%', border: `1px solid ${accent}50`, borderRadius: 4, pointerEvents: 'none' }} />
                <div style={{
                    position: 'absolute', top: '12%', left: 0, right: 0, padding: '0 8%', textAlign: 'center', color: '#fff',
                }}>
                    {f.headline && (
                        <div style={{ color: accent, fontSize: 'min(11px, 2.6vw)', letterSpacing: '0.4em', marginBottom: 12, textTransform: 'uppercase', fontWeight: 600 }}>
                            ◆ {f.headline} ◆
                        </div>
                    )}
                    <div style={{ fontSize: 'min(40px, 9vw)', fontWeight: 900, lineHeight: 1, fontStyle: 'italic' }}>
                        Save<br /><span style={{ color: accent }}>{f.discountPct}%</span>
                    </div>
                </div>
                {f.urgency && (
                    <div style={{
                        position: 'absolute', left: 0, right: 0, bottom: '20%', textAlign: 'center', color: accent,
                        fontSize: 'min(11px, 2.6vw)', letterSpacing: '0.3em', fontWeight: 600, textTransform: 'uppercase',
                    }}>◆ {f.urgency} ◆</div>
                )}
                {ShopNowPill}
            </div>
        );
    }

    if (frame.style === 'storybogo') {
        const ribbonText = `BUY ${f.buyQty} · GET ${f.getQty} FREE`;
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#EAF7F0' }}>
                {ImageBase}
                {/* Top ribbon */}
                <div style={{
                    position: 'absolute', top: '8%', left: '6%', right: '6%',
                    background: accent, color: '#fff',
                    padding: '12px 8px', textAlign: 'center', transform: 'rotate(-2deg)',
                    boxShadow: '0 8px 18px -6px rgba(0,0,0,0.35)',
                }}>
                    <span style={{ fontSize: 'min(15px, 3.6vw)', fontWeight: 900, letterSpacing: '0.12em' }}>{ribbonText}</span>
                </div>
                {f.urgency && (
                    <div style={{
                        position: 'absolute', left: 0, right: 0, bottom: '20%', textAlign: 'center', color: secondary,
                        fontSize: 'min(13px, 3vw)', fontWeight: 700,
                    }}>🌿 {f.urgency}</div>
                )}
                {ShopNowPill}
            </div>
        );
    }

    if (frame.style === 'storysoon') {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#000' }}>
                {ImageBase}
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
                <div style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-3deg)',
                    background: accent, color: '#fff',
                    padding: '14px 28px',
                    fontSize: 'min(22px, 5vw)', fontWeight: 900, letterSpacing: '0.18em',
                    textTransform: 'uppercase', textAlign: 'center', whiteSpace: 'nowrap',
                    boxShadow: `8px 8px 0 ${secondary}, 0 14px 30px -8px rgba(0,0,0,0.5)`,
                }}>{f.headline || 'COMING SOON'}</div>
                {f.urgency && (
                    <div style={{
                        position: 'absolute', left: 0, right: 0, bottom: '22%', textAlign: 'center', color: '#fff',
                        fontSize: 'min(11px, 2.6vw)', letterSpacing: '0.22em', fontWeight: 700, textTransform: 'uppercase',
                    }}>{f.urgency}</div>
                )}
                {ShopNowPill}
            </div>
        );
    }

    return <div style={{ width: '100%', height: '100%', position: 'relative' }}>{ImageBase}</div>;
}

// ---------------------------------------------------------------------------
// Canvas compositor — bakes the selected frame onto the product image so the
// posted JPEG actually shows the overlay on Instagram. The CSS preview is
// only client-side; Instagram receives whatever URL we send, so we have to
// pre-render a real image when a frame is chosen.
// ---------------------------------------------------------------------------
function loadCorsImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Image load failed (CORS?)'));
        img.src = url;
    });
}
function drawImageCover(ctx, img, x, y, w, h) {
    const ar = img.width / img.height;
    const ta = w / h;
    let sx, sy, sw, sh;
    if (ar > ta) { sh = img.height; sw = sh * ta; sx = (img.width - sw) / 2; sy = 0; }
    else { sw = img.width; sh = sw / ta; sx = 0; sy = (img.height - sh) / 2; }
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}
function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}
// Mirrors the React <ProductCollage> renderer onto a canvas. All sizes are
// proportional to S so this works at any resolution. Layouts: bundle (1-4
// adaptive), stacked, polaroid, carousel, mosaic.
async function renderBundleCollageBase(ctx, S, products, template) {
    const all = (products || []).filter((p) => p?.imageUrl);
    if (all.length === 0) {
        const g = ctx.createLinearGradient(0, 0, S, S);
        g.addColorStop(0, '#fbcfe8'); g.addColorStop(0.4, '#f9a8d4'); g.addColorStop(1, '#fde68a');
        ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
        return;
    }
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, S, S);

    const maxItems = (template === 'carousel' || template === 'mosaic') ? 5 : 4;
    const items = all.slice(0, maxItems);
    const imgs = await Promise.all(items.map((p) => loadCorsImage(p.imageUrl).catch(() => null)));
    const extra = Math.max(0, all.length - items.length);

    const PAD = S * 0.06;
    const R = S * 0.04;
    const GAP = S * 0.025;
    const inner = S - PAD * 2;

    const drawTile = (img, x, y, w, h, r = R) => {
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.10)';
        ctx.shadowBlur = S * 0.014; ctx.shadowOffsetY = S * 0.006;
        ctx.fillStyle = '#fff';
        roundRectPath(ctx, x, y, w, h, r); ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.beginPath(); roundRectPath(ctx, x, y, w, h, r); ctx.clip();
        if (img) drawImageCover(ctx, img, x, y, w, h);
        else { ctx.fillStyle = '#f5f5f4'; ctx.fillRect(x, y, w, h); }
        ctx.restore();
    };
    const drawMoreBadge = (x, y, w, h, r, n) => {
        ctx.save();
        ctx.beginPath(); roundRectPath(ctx, x, y, w, h, r); ctx.clip();
        ctx.fillStyle = 'rgba(28,25,23,0.55)'; ctx.fillRect(x, y, w, h);
        ctx.fillStyle = '#fff';
        ctx.font = `600 ${S * 0.06}px system-ui, sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(`+${n}`, x + w / 2, y + h / 2);
        ctx.restore();
    };

    if (template === 'stacked') {
        const size = inner * 0.55;
        const cx = S / 2, cy = S / 2;
        const offsets = [
            { rotate: 0, x: 0, y: 0 },
            { rotate: -11, x: -S * 0.12, y: -S * 0.005 },
            { rotate: 11, x: S * 0.115, y: S * 0.005 },
            { rotate: -5, x: -S * 0.022, y: S * 0.05 },
        ];
        const stItems = items.slice(0, 4);
        for (let idx = stItems.length - 1; idx >= 0; idx--) {
            const lay = offsets[idx] || offsets[3];
            const img = imgs[idx];
            const border = S * 0.012;
            ctx.save();
            ctx.translate(cx + lay.x, cy + lay.y);
            ctx.rotate(lay.rotate * Math.PI / 180);
            const dx = -size / 2, dy = -size / 2;
            ctx.shadowColor = idx === 0 ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.10)';
            ctx.shadowBlur = idx === 0 ? S * 0.028 : S * 0.013;
            ctx.shadowOffsetY = idx === 0 ? S * 0.013 : S * 0.005;
            ctx.fillStyle = '#fff';
            roundRectPath(ctx, dx, dy, size, size, R); ctx.fill();
            ctx.shadowColor = 'transparent';
            ctx.beginPath();
            roundRectPath(ctx, dx + border, dy + border, size - border * 2, size - border * 2, Math.max(0, R - border));
            ctx.clip();
            if (img) drawImageCover(ctx, img, dx + border, dy + border, size - border * 2, size - border * 2);
            else { ctx.fillStyle = '#f5f5f4'; ctx.fillRect(dx + border, dy + border, size - border * 2, size - border * 2); }
            ctx.restore();
        }
        return;
    }

    if (template === 'polaroid') {
        const polItems = items.slice(0, 4);
        const tileH = inner * 0.82;
        const tileW = tileH * 0.78;
        const rotations = [-8, 5, -4, 9];
        const overlap = tileW * 0.2;
        const totalW = tileW * polItems.length - overlap * (polItems.length - 1);
        const startX = (S - totalW) / 2;
        const cy = S / 2;
        for (let i = polItems.length - 1; i >= 0; i--) {
            const img = imgs[i];
            const px = startX + i * (tileW - overlap);
            const py = cy - tileH / 2;
            const rot = rotations[i] || 0;
            ctx.save();
            ctx.translate(px + tileW / 2, py + tileH / 2);
            ctx.rotate(rot * Math.PI / 180);
            const dx = -tileW / 2, dy = -tileH / 2;
            ctx.shadowColor = 'rgba(0,0,0,0.12)'; ctx.shadowBlur = S * 0.014; ctx.shadowOffsetY = S * 0.006;
            ctx.fillStyle = '#fff'; ctx.fillRect(dx, dy, tileW, tileH);
            ctx.shadowColor = 'transparent';
            const padTL = tileW * 0.05;
            const padB = tileW * 0.16;
            const ix = dx + padTL, iy = dy + padTL;
            const iw = tileW - padTL * 2;
            const ih = tileH - padTL - padB;
            ctx.beginPath(); ctx.rect(ix, iy, iw, ih); ctx.clip();
            if (img) drawImageCover(ctx, img, ix, iy, iw, ih);
            else { ctx.fillStyle = '#f5f5f4'; ctx.fillRect(ix, iy, iw, ih); }
            ctx.restore();
        }
        return;
    }

    if (template === 'carousel') {
        const carItems = items.slice(0, 5);
        const center = Math.floor(carItems.length / 2);
        const layouts = carItems.map((_, i) => {
            const dist = Math.abs(i - center);
            const isCenter = dist === 0;
            const tileH = inner * (isCenter ? 1 : 0.78);
            const tileW = tileH * 0.78;
            return { dist, tileH, tileW };
        });
        const overlapFactor = 0.22;
        let totalW = 0;
        layouts.forEach((l, i) => { totalW += i === 0 ? l.tileW : l.tileW * (1 - overlapFactor); });
        const startX = (S - totalW) / 2;
        const xs = [];
        layouts.forEach((l, i) => {
            xs.push(i === 0 ? startX : xs[i - 1] + layouts[i - 1].tileW * (1 - overlapFactor));
        });
        const cy = S / 2;
        const order = layouts.map((_, i) => i).sort((a, b) => layouts[b].dist - layouts[a].dist);
        for (const i of order) {
            const l = layouts[i];
            const img = imgs[i];
            const px = xs[i];
            const py = cy - l.tileH / 2;
            const border = S * 0.009;
            const opacity = 1 - l.dist * 0.08;
            ctx.save();
            ctx.globalAlpha = opacity;
            ctx.shadowColor = `rgba(0,0,0,${0.14 - l.dist * 0.03})`;
            ctx.shadowBlur = S * 0.015; ctx.shadowOffsetY = S * 0.005;
            ctx.fillStyle = '#fff';
            roundRectPath(ctx, px, py, l.tileW, l.tileH, R); ctx.fill();
            ctx.shadowColor = 'transparent';
            ctx.beginPath();
            roundRectPath(ctx, px + border, py + border, l.tileW - border * 2, l.tileH - border * 2, Math.max(0, R - border));
            ctx.clip();
            if (img) drawImageCover(ctx, img, px + border, py + border, l.tileW - border * 2, l.tileH - border * 2);
            else { ctx.fillStyle = '#f5f5f4'; ctx.fillRect(px + border, py + border, l.tileW - border * 2, l.tileH - border * 2); }
            ctx.restore();
        }
        return;
    }

    if (template === 'mosaic') {
        const mItems = items.slice(0, 5);
        if (mItems.length === 1) {
            const w = inner * 0.95, h = inner;
            drawTile(imgs[0], (S - w) / 2, (S - h) / 2, w, h, R);
            return;
        }
        const totalFlex = 1.6 + 1;
        const colGap = GAP * 0.85;
        const leftW = inner * (1.6 / totalFlex) - colGap / 2;
        const rightW = inner * (1 / totalFlex) - colGap / 2;
        const rightX = PAD + leftW + colGap;
        drawTile(imgs[0], PAD, PAD, leftW, inner, R);
        const rightItems = mItems.slice(1);
        const rowGap = GAP * 0.85;
        const rowH = (inner - rowGap * (rightItems.length - 1)) / rightItems.length;
        rightItems.forEach((_, idx) => {
            const i = idx + 1;
            const y = PAD + idx * (rowH + rowGap);
            drawTile(imgs[i], rightX, y, rightW, rowH, R);
            if (idx === rightItems.length - 1 && extra > 0) {
                drawMoreBadge(rightX, y, rightW, rowH, R, extra);
            }
        });
        return;
    }

    // bundle (default) — adaptive
    const bItems = items.slice(0, 4);
    if (bItems.length === 1) {
        const w = inner * 0.95, h = inner;
        drawTile(imgs[0], (S - w) / 2, (S - h) / 2, w, h, R);
        return;
    }
    if (bItems.length === 2) {
        const each = (inner - GAP) / 2;
        for (let i = 0; i < 2; i++) drawTile(imgs[i], PAD + i * (each + GAP), PAD, each, inner, R);
        return;
    }
    if (bItems.length === 3) {
        const totalFlex = 1.4 + 1;
        const leftW = inner * (1.4 / totalFlex) - GAP / 2;
        const rightW = inner * (1 / totalFlex) - GAP / 2;
        drawTile(imgs[0], PAD, PAD, leftW, inner, R);
        const rightX = PAD + leftW + GAP;
        const rowH = (inner - GAP) / 2;
        drawTile(imgs[1], rightX, PAD, rightW, rowH, R);
        drawTile(imgs[2], rightX, PAD + rowH + GAP, rightW, rowH, R);
        return;
    }
    const each = (inner - GAP) / 2;
    for (let i = 0; i < 4; i++) {
        const col = i % 2, row = Math.floor(i / 2);
        drawTile(imgs[i], PAD + col * (each + GAP), PAD + row * (each + GAP), each, each, R);
    }
    if (extra > 0) {
        const x = PAD + (each + GAP);
        const y = PAD + (each + GAP);
        drawMoreBadge(x, y, each, each, R, extra);
    }
}

async function renderFramedImageToBlob({ frame, productImageUrl, fields = {}, bundle = null }) {
    const isBundle = !!(bundle && Array.isArray(bundle.products) && bundle.products.length > 0);
    const hasFrame = !!(frame && frame.style !== 'none');
    if (!isBundle && (!hasFrame || !productImageUrl)) return null;
    const S = 1080;
    const canvas = document.createElement('canvas');
    canvas.width = S; canvas.height = S;
    const ctx = canvas.getContext('2d');

    if (isBundle) {
        await renderBundleCollageBase(ctx, S, bundle.products, bundle.collageTemplate || 'bundle');
    } else {
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, S, S);
        const img = await loadCorsImage(productImageUrl);
        drawImageCover(ctx, img, 0, 0, S, S);
    }

    if (!hasFrame) {
        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject(new Error('Canvas toBlob failed (tainted?)'));
            }, 'image/jpeg', 0.92);
        });
    }

    const f = { ...(frame.defaults || {}), ...fields };
    const accent = frame.accent || '#1f2937';
    const secondary = frame.secondary || '#fff';

    if (frame.style === 'burst') {
        ctx.save();
        ctx.translate(S * 0.18, S * 0.2);
        ctx.rotate(-38 * Math.PI / 180);
        const ribW = S * 0.85, ribH = S * 0.11;
        ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 14; ctx.shadowOffsetY = 4;
        ctx.fillStyle = accent; ctx.fillRect(-ribW / 2, -ribH / 2, ribW, ribH);
        ctx.shadowColor = 'transparent';
        ctx.fillStyle = secondary;
        ctx.font = `800 ${S * 0.05}px system-ui, sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(`${f.discountPct}% OFF`, 0, 0);
        ctx.restore();
        if (f.urgency) {
            const bandH = S * 0.07;
            ctx.fillStyle = 'rgba(0,0,0,0.75)';
            ctx.fillRect(0, S - bandH, S, bandH);
            ctx.fillStyle = '#fff';
            ctx.font = `700 ${S * 0.024}px system-ui, sans-serif`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(String(f.urgency).toUpperCase(), S / 2, S - bandH / 2);
        }
    } else if (frame.style === 'badge') {
        if (f.urgency) {
            const stripH = S * 0.05;
            ctx.fillStyle = secondary; ctx.fillRect(0, 0, S, stripH);
            ctx.fillStyle = accent;
            ctx.font = `700 ${S * 0.022}px system-ui, sans-serif`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(String(f.urgency).toUpperCase(), S / 2, stripH / 2);
        }
        const cx = S * 0.78, cy = S * 0.22, r = S * 0.15;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(-12 * Math.PI / 180);
        ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.arc(0, 0, r + 8, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = accent;
        ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 16; ctx.shadowOffsetY = 6;
        ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.fillStyle = secondary;
        ctx.font = `900 ${S * 0.07}px system-ui, sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(`${f.discountPct}%`, 0, -r * 0.18);
        ctx.font = `700 ${S * 0.024}px system-ui, sans-serif`;
        ctx.fillText('OFF', 0, r * 0.32);
        ctx.restore();
    } else if (frame.style === 'bogo') {
        const bannerH = S * 0.09;
        ctx.fillStyle = accent; ctx.fillRect(0, 0, S, bannerH);
        ctx.fillStyle = '#fff';
        ctx.font = `800 ${S * 0.038}px system-ui, sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(`BUY ${f.buyQty} GET ${f.getQty} FREE`, S / 2, bannerH / 2);
        if (f.urgency) {
            const bandH = S * 0.06;
            ctx.fillStyle = secondary; ctx.fillRect(0, S - bandH, S, bandH);
            ctx.fillStyle = '#fff';
            ctx.font = `600 ${S * 0.025}px system-ui, sans-serif`;
            ctx.fillText(f.urgency, S / 2, S - bandH / 2);
        }
    } else if (frame.style === 'spotlight') {
        const gh = S * 0.42;
        const grad = ctx.createLinearGradient(0, S - gh, 0, S);
        grad.addColorStop(0, 'rgba(0,0,0,0)');
        grad.addColorStop(1, 'rgba(0,0,0,0.88)');
        ctx.fillStyle = grad; ctx.fillRect(0, S - gh, S, gh);
        ctx.fillStyle = '#fff';
        ctx.font = `700 ${S * 0.045}px system-ui, sans-serif`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText(f.headline || '', S * 0.05, S * 0.84);
        const ctaText = `${(f.cta || 'SHOP NOW').toUpperCase()} →`;
        ctx.font = `800 ${S * 0.028}px system-ui, sans-serif`;
        const tw = ctx.measureText(ctaText).width;
        const padX = S * 0.035, padY = S * 0.018;
        const pillW = tw + padX * 2, pillH = S * 0.028 + padY * 2;
        const pillX = S * 0.05, pillY = S * 0.9;
        ctx.fillStyle = secondary;
        roundRectPath(ctx, pillX, pillY, pillW, pillH, pillH / 2);
        ctx.fill();
        ctx.fillStyle = accent;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(ctaText, pillX + pillW / 2, pillY + pillH / 2);
    } else if (frame.style === 'soon') {
        ctx.fillStyle = 'rgba(0,0,0,0.4)'; ctx.fillRect(0, 0, S, S);
        ctx.save();
        ctx.translate(S / 2, S / 2);
        ctx.rotate(-2 * Math.PI / 180);
        const text = String(f.headline || 'COMING SOON').toUpperCase();
        ctx.font = `900 ${S * 0.055}px system-ui, sans-serif`;
        const tw = ctx.measureText(text).width;
        const boxW = tw + S * 0.1, boxH = S * 0.11;
        ctx.shadowColor = 'rgba(0,0,0,0.4)'; ctx.shadowBlur = 22; ctx.shadowOffsetY = 8;
        ctx.fillStyle = accent;
        ctx.fillRect(-boxW / 2, -boxH / 2, boxW, boxH);
        ctx.shadowColor = 'transparent';
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(text, 0, 0);
        ctx.restore();
        if (f.urgency) {
            ctx.fillStyle = '#fff';
            ctx.font = `600 ${S * 0.028}px system-ui, sans-serif`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(String(f.urgency).toUpperCase(), S / 2, S * 0.62);
        }
    }

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas toBlob failed (tainted?)'));
        }, 'image/jpeg', 0.92);
    });
}

// --- 9:16 story compositor ---
// Renders the chosen STORY_FRAMES style as a 1080×1920 JPEG so the actual
// posted Instagram story carries the frame artwork (not just the raw product).
async function renderStoryFramedImageToBlob({ frame, productImageUrl, fields = {} }) {
    if (!frame || frame.style === 'none' || !productImageUrl) return null;
    const W = 1080, H = 1920;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H);
    const img = await loadCorsImage(productImageUrl);
    drawImageCover(ctx, img, 0, 0, W, H);
    const f = { ...(frame.defaults || {}), ...fields };
    const accent = frame.accent || '#1f2937';
    const secondary = frame.secondary || '#fff';

    if (frame.style === 'splash') {
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.3)'; ctx.shadowBlur = 26; ctx.shadowOffsetY = 10;
        ctx.fillStyle = accent;
        ctx.beginPath();
        ctx.moveTo(-W * 0.15, -H * 0.05);
        ctx.lineTo(W * 0.85, -H * 0.05);
        ctx.quadraticCurveTo(W * 1.05, H * 0.25, W * 0.7, H * 0.5);
        ctx.quadraticCurveTo(W * 0.4, H * 0.6, -W * 0.05, H * 0.45);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        ctx.fillStyle = '#fff';
        ctx.font = `900 ${W * 0.18}px system-ui, sans-serif`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'top';
        ctx.fillText(`${f.discountPct}%`, W * 0.08, H * 0.06);
        ctx.fillText('OFF', W * 0.08, H * 0.06 + W * 0.19);
        if (f.urgency) {
            const text = String(f.urgency).toUpperCase();
            ctx.font = `800 ${W * 0.028}px system-ui, sans-serif`;
            const tw = ctx.measureText(text).width;
            const padX = W * 0.03, padY = W * 0.014;
            const pillW = tw + padX * 2, pillH = W * 0.028 + padY * 2;
            const px = (W - pillW) / 2, py = H * 0.78;
            ctx.shadowColor = 'rgba(0,0,0,0.3)'; ctx.shadowBlur = 14; ctx.shadowOffsetY = 4;
            ctx.fillStyle = '#fff';
            roundRectPath(ctx, px, py, pillW, pillH, pillH / 2); ctx.fill();
            ctx.shadowColor = 'transparent';
            ctx.fillStyle = secondary;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(text, px + pillW / 2, py + pillH / 2);
        }
    } else if (frame.style === 'midnight') {
        ctx.fillStyle = 'rgba(11,15,26,0.78)'; ctx.fillRect(0, 0, W, H);
        ctx.strokeStyle = `${accent}50`; ctx.lineWidth = 2;
        const m = W * 0.06; ctx.strokeRect(m, m, W - m * 2, H - m * 2);
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        if (f.headline) {
            ctx.fillStyle = accent;
            ctx.font = `600 ${W * 0.028}px system-ui, sans-serif`;
            ctx.fillText(`◆ ${String(f.headline).toUpperCase()} ◆`, W / 2, H * 0.18);
        }
        ctx.fillStyle = '#fff';
        ctx.font = `italic 900 ${W * 0.2}px system-ui, sans-serif`;
        ctx.fillText('Save', W / 2, H * 0.34);
        ctx.fillStyle = accent;
        ctx.fillText(`${f.discountPct}%`, W / 2, H * 0.5);
        if (f.urgency) {
            ctx.fillStyle = accent;
            ctx.font = `600 ${W * 0.028}px system-ui, sans-serif`;
            ctx.fillText(`◆ ${String(f.urgency).toUpperCase()} ◆`, W / 2, H * 0.78);
        }
    } else if (frame.style === 'storybogo') {
        ctx.save();
        ctx.translate(W / 2, H * 0.13);
        ctx.rotate(-2 * Math.PI / 180);
        ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 18; ctx.shadowOffsetY = 8;
        ctx.fillStyle = accent;
        const rW = W * 0.88, rH = W * 0.13;
        ctx.fillRect(-rW / 2, -rH / 2, rW, rH);
        ctx.shadowColor = 'transparent';
        ctx.fillStyle = '#fff';
        ctx.font = `900 ${W * 0.04}px system-ui, sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(`BUY ${f.buyQty} · GET ${f.getQty} FREE`, 0, 0);
        ctx.restore();
        if (f.urgency) {
            ctx.fillStyle = secondary;
            ctx.font = `700 ${W * 0.034}px system-ui, sans-serif`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(`🌿 ${f.urgency}`, W / 2, H * 0.78);
        }
    } else if (frame.style === 'storysoon') {
        ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, W, H);
        ctx.save();
        ctx.translate(W / 2, H / 2);
        ctx.rotate(-3 * Math.PI / 180);
        const text = String(f.headline || 'COMING SOON').toUpperCase();
        ctx.font = `900 ${W * 0.06}px system-ui, sans-serif`;
        const tw = ctx.measureText(text).width;
        const boxW = tw + W * 0.1, boxH = W * 0.14;
        ctx.fillStyle = secondary;
        ctx.fillRect(-boxW / 2 + 8, -boxH / 2 + 8, boxW, boxH);
        ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 30; ctx.shadowOffsetY = 14;
        ctx.fillStyle = accent;
        ctx.fillRect(-boxW / 2, -boxH / 2, boxW, boxH);
        ctx.shadowColor = 'transparent';
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(text, 0, 0);
        ctx.restore();
        if (f.urgency) {
            ctx.fillStyle = '#fff';
            ctx.font = `700 ${W * 0.028}px system-ui, sans-serif`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(String(f.urgency).toUpperCase(), W / 2, H * 0.65);
        }
    }

    // Shared SHOP NOW pill at the bottom.
    {
        const text = `${(f.cta || 'SHOP NOW').toUpperCase()} →`;
        ctx.font = `800 ${W * 0.03}px system-ui, sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        const tw = ctx.measureText(text).width;
        const padX = W * 0.05, padY = W * 0.02;
        const pillW = Math.max(tw + padX * 2, W * 0.55);
        const pillH = W * 0.03 + padY * 2;
        const px = (W - pillW) / 2, py = H * 0.92 - pillH / 2;
        ctx.shadowColor = 'rgba(0,0,0,0.3)'; ctx.shadowBlur = 14; ctx.shadowOffsetY = 4;
        ctx.fillStyle = '#fff';
        roundRectPath(ctx, px, py, pillW, pillH, pillH / 2); ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.fillStyle = '#0f172a';
        ctx.fillText(text, px + pillW / 2, py + pillH / 2);
    }

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas toBlob failed (tainted?)'));
        }, 'image/jpeg', 0.92);
    });
}

// --- ComposerSheet (full-screen bottom sheet) ---
function ComposerSheet({
    post, media, weekStart, mediaMap, onClose, onSave, onDelete, allMedia = [],
    initialDayOffset = null, initialHour = null,
    accountHandle = '', accountDisplayName = '', accountAvatarUrl = '',
}) {
    const displayHandle = (accountHandle || '').trim() || 'your.handle';
    const avatarOk = !!accountAvatarUrl;

    // --- Date/time validation -------------------------------------------------
    // Scheduling rule: you can't schedule into the past. On TODAY, the earliest
    // available hour is the next HOURS slot after "now". Past days are fully
    // disabled. If all of today's slots are already gone (e.g. it's 8 PM), the
    // first future day becomes the smart default.
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // Float "now" so we can compare arbitrary minute-level times.
    const currentHour = now.getHours() + now.getMinutes() / 60;
    // Earliest valid time on today — 1 minute past now so we never publish
    // exactly "right now" by accident. `undefined` once today is over.
    const firstValidTodayHour = (() => {
        const next = currentHour + 1 / 60;
        return next < 24 ? next : undefined;
    })();

    // Index of today within the 7-day weekStart window (usually 3 with
    // centeredWeekStart, but be defensive if someone navigates the week).
    const todayOffsetInWindow = (() => {
        for (let i = 0; i < 7; i++) {
            const d = addDays(weekStart, i);
            if (d.toDateString() === now.toDateString()) return i;
        }
        return -1;
    })();

    // Is an offset (0..6) in the past / today / future?
    const classifyOffset = (i) => {
        const d = addDays(weekStart, i);
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        if (dayStart < todayStart) return 'past';
        if (dayStart.getTime() === todayStart.getTime()) return 'today';
        return 'future';
    };

    // Is TODAY still bookable? (false only when it's already past ~23:55)
    const todayHasValidSlot = firstValidTodayHour !== undefined;

    // Smart default offset for a new post: today if slots remain, else first future day.
    const defaultDayOffset = (() => {
        if (todayOffsetInWindow >= 0 && todayHasValidSlot) return todayOffsetInWindow;
        for (let i = 0; i < 7; i++) {
            if (classifyOffset(i) === 'future') return i;
        }
        return Math.max(0, todayOffsetInWindow);
    })();
    const defaultHour = (() => {
        const cls = classifyOffset(defaultDayOffset);
        if (cls === 'today') return firstValidTodayHour ?? 10;
        return 10; // 10:00 AM — a nice default for future days (matches HOURS slot 20)
    })();
    // ---------------------------------------------------------------------------

    const [caption, setCaption] = useState(post?.caption || '');
    // For server-backed posts (edit flow) `post.mediaId` is null but `post.media`
    // has the URL — find a library item with the same URL to pre-select it.
    // No `allMedia[0]` fallback here: it would silently override a server post's
    // real product with the first uploaded library item.
    const resolvedInitialMediaId = (
        post?.mediaId
        || (post?.media?.url && allMedia.find((m) => m.url === post.media.url)?.id)
        || media?.id
        || null
    );
    const [selectedMediaId, setSelectedMediaId] = useState(resolvedInitialMediaId);
    const [selectedDay, setSelectedDay] = useState(post?.dayOffset ?? initialDayOffset ?? defaultDayOffset);
    const [selectedHour, setSelectedHour] = useState(post?.hour ?? initialHour ?? defaultHour);
    const [selectedFrameId, setSelectedFrameId] = useState(post?.frameId || 'none');
    const selectedFrame = POST_FRAMES.find((f) => f.id === selectedFrameId) || POST_FRAMES[0];
    const isEditing = !!post;
    // Fall back chain: library item → existing post's stored media → the
    // dragged-in product/media object passed via the `media` prop. The last
    // step is what makes products show up in the composer (they aren't in
    // mediaMap, which only holds uploaded library items).
    const selectedMedia = mediaMap.get(selectedMediaId) || post?.media || media || null;
    const publishDate = addDays(weekStart, selectedDay);
    const publishStr = publishDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    const timeStr = formatHour(selectedHour);

    // When the user changes day, if the current hour is no longer valid on the
    // new day (e.g. they picked "today" with a past hour still in state),
    // auto-advance to the first valid slot. Skip the initial mount so editing
    // a past-time post displays the originally saved time (the red warning
    // banner below tells the user it's invalid).
    const dayChangedRef = useRef(false);
    useEffect(() => {
        if (!dayChangedRef.current) { dayChangedRef.current = true; return; }
        const cls = classifyOffset(selectedDay);
        if (cls === 'today' && firstValidTodayHour !== undefined && selectedHour < firstValidTodayHour) {
            setSelectedHour(firstValidTodayHour);
        }
    }, [selectedDay]); // eslint-disable-line react-hooks/exhaustive-deps

    // Final computed publish Date — used for the save guard.
    const publishDateTime = (() => {
        const d = new Date(publishDate);
        const [hh, mm] = hourToHM(selectedHour);
        d.setHours(hh, mm, 0, 0);
        return d;
    })();
    const isPastSelection = publishDateTime <= now;

    // Drag-to-resize state. 12% from top ≈ 88% tall sheet on open — big enough
    // to work in comfortably while still leaving a peek of the calendar behind.
    const [sheetTop, setSheetTop] = useState(0); // % from top — fullscreen by default
    const [dragging, setDragging] = useState(false);
    const dragStartRef = useRef({ y: 0, startTop: 12 });
    // Exit-animation state. When `closing` flips to true, the sheet + backdrop
    // play their slide-down/fade-out animations; once the sheet's animation
    // ends, we fire `pendingActionRef.current` (save/delete) or fall back to
    // onClose. This mirrors the open animation so close feels symmetric.
    const [closing, setClosing] = useState(false);
    const pendingActionRef = useRef(null);
    const requestClose = (action) => {
        if (closing) return;
        pendingActionRef.current = typeof action === 'function' ? action : null;
        setClosing(true);
    };
    const handleExitEnd = (e) => {
        // Ignore bubbling animation events from descendants.
        if (e && e.target !== e.currentTarget) return;
        if (!closing) return;
        const action = pendingActionRef.current;
        pendingActionRef.current = null;
        if (action) action();
        else onClose?.();
    };

    const onPillPointerDown = (e) => {
        e.preventDefault();
        const cy = e.clientY ?? e.touches?.[0]?.clientY;
        if (cy == null) return;
        dragStartRef.current = { y: cy, startTop: sheetTop };
        setDragging(true);
    };

    useEffect(() => {
        if (!dragging) return;
        const onMove = (e) => {
            const cy = e.clientY ?? e.touches?.[0]?.clientY;
            if (cy == null) return;
            const dy = cy - dragStartRef.current.y;
            const dvh = (dy / window.innerHeight) * 100;
            const newTop = Math.max(5, Math.min(70, dragStartRef.current.startTop + dvh));
            setSheetTop(newTop);
        };
        const onUp = () => {
            setDragging(false);
            // Close if dragged past 65% — animate the rest of the way down.
            if (sheetTop > 65) requestClose();
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, [dragging, sheetTop, onClose]);

    return (
        <>
            <div
                onClick={() => requestClose()}
                style={{
                    position: 'fixed', inset: 0, zIndex: 1000,
                    background: 'rgba(28,25,23,0.35)', backdropFilter: 'blur(3px)',
                    animation: dragging
                        ? 'none'
                        : closing
                            ? 'igBackdropFadeOut 300ms ease both'
                            : 'igBackdropFade 280ms ease both',
                }}
            />
            <div
                onAnimationEnd={handleExitEnd}
                style={{
                    position: 'fixed', top: `${sheetTop}%`, bottom: 0, left: 0, right: 0, zIndex: 1001,
                    background: '#fff', borderRadius: 0,
                    boxShadow: '0 -8px 40px -8px rgba(0,0,0,0.2)',
                    transformOrigin: 'center center',
                    willChange: 'transform, opacity',
                    animation: dragging
                        ? 'none'
                        : closing
                            ? 'igPostZoomOut 450ms cubic-bezier(0.55, 0, 0.8, 0.2) both'
                            : 'igPostZoomIn 450ms cubic-bezier(0.22, 1.2, 0.36, 1) both',
                    display: 'flex', flexDirection: 'column',
                    transition: dragging ? 'none' : 'top 300ms cubic-bezier(.2,.8,.2,1)',
                }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #f5f5f4' }}>
                    <div>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#a8a29e', marginBottom: 2 }}>{isEditing ? 'Edit' : 'Compose'}</div>
                        <h3 style={{ fontSize: 22, fontWeight: 600, color: '#1f2937', margin: 0, letterSpacing: '-0.02em' }}>{isEditing ? 'Edit post' : 'New post'}</h3>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {isEditing && (
                            <button onClick={() => requestClose(() => onDelete?.(post.id))} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                                Delete
                            </button>
                        )}
                        <button onClick={() => requestClose()} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e7e5e4', background: '#fff', color: '#44403c', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                        <button
                            onClick={() => {
                                if (isPastSelection) {
                                    toast.error('Pick a time in the future to schedule this post.');
                                    return;
                                }
                                requestClose(() => onSave?.({ id: post?.id, mediaId: selectedMediaId, dayOffset: selectedDay, hour: selectedHour, caption, draggedMedia: media || selectedMedia || null, frameId: selectedFrameId }));
                            }}
                            disabled={isPastSelection}
                            title={isPastSelection ? 'Selected time is in the past' : undefined}
                            style={{
                                padding: '8px 18px', borderRadius: 8,
                                border: '1px solid ' + (isPastSelection ? '#d6d3d1' : '#1f2937'),
                                background: isPastSelection ? '#e7e5e4' : '#1f2937',
                                color: isPastSelection ? '#a8a29e' : '#fff',
                                fontSize: 13, fontWeight: 600,
                                cursor: isPastSelection ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {isEditing ? 'Save changes' : 'Schedule post'}
                        </button>
                    </div>
                </div>
                {/* Body — 3 column grid */}
                <div style={{ flex: 1, overflow: 'hidden', display: 'grid', gridTemplateColumns: 'auto 1fr 380px', gap: 24, padding: 24, minHeight: 0 }}>
                    {/* Col 1: Frame templates — overlay decorations applied on top of the post image */}
                    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, flexShrink: 0 }}>
                            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e' }}>Frames</div>
                            <span style={{ fontSize: 10, color: '#a8a29e' }}>{selectedFrame.name}</span>
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 14,
                            width: 520,
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            paddingRight: 4,
                            alignContent: 'start',
                        }}>
                            {POST_FRAMES.map((fr) => {
                                const isSel = fr.id === selectedFrameId;
                                return (
                                    <button
                                        key={fr.id}
                                        type="button"
                                        onClick={() => setSelectedFrameId(fr.id)}
                                        title={fr.name}
                                        style={{
                                            position: 'relative', padding: 0, borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                                            aspectRatio: '1',
                                            border: isSel ? '2px solid #111827' : '1px solid rgba(214,211,209,0.7)',
                                            outline: isSel ? '2px solid #111827' : 'none', outlineOffset: 2,
                                            transform: isSel ? 'scale(0.97)' : 'none', transition: 'all 150ms ease',
                                            background: '#fff',
                                        }}
                                    >
                                        <PostFrameRender frame={fr} productImage={selectedMedia?.productImageUrl || selectedMedia?.url} productTitle={selectedMedia?.title} bundle={selectedMedia?.bundle} />
                                        <div style={{
                                            position: 'absolute', bottom: 0, left: 0, right: 0,
                                            padding: '14px 6px 4px',
                                            background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.75))',
                                            color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                                            textTransform: 'uppercase', textAlign: 'center',
                                            pointerEvents: 'none',
                                        }}>
                                            {fr.name}
                                        </div>
                                        {isSel && (
                                            <div style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: 99, background: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {/* Col 2: Caption + schedule */}
                    <div style={{ minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e', marginBottom: 10 }}>Caption</div>
                        <textarea value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 2200))} placeholder="Write something lovely…" rows={24} maxLength={2200} style={{
                            width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid #e7e5e4', fontSize: 14, color: '#1f2937', resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.6, boxSizing: 'border-box',
                        }} onFocus={(e) => e.target.style.borderColor = '#1f2937'} onBlur={(e) => e.target.style.borderColor = '#e7e5e4'} />
                        <div style={{ textAlign: 'right', fontSize: 11, color: caption.length >= 2200 ? '#b91c1c' : '#a8a29e', marginTop: 4 }}>{caption.length} / 2200</div>
                        <div style={{ marginTop: 20 }}>
                            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e', marginBottom: 10 }}>Schedule</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 10 }}>
                                <ThemedSelect
                                    icon={(
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                    )}
                                    label="Day"
                                    value={selectedDay}
                                    onChange={(v) => setSelectedDay(Number(v))}
                                >
                                    {Array.from({ length: 7 }).map((_, i) => {
                                        const d = addDays(weekStart, i);
                                        const cls = classifyOffset(i);
                                        // Disable past days entirely, and today if no valid slot remains.
                                        const disabled = cls === 'past' || (cls === 'today' && !todayHasValidSlot);
                                        const label = `${DAY_LABELS[d.getDay()]} ${d.getDate()}`;
                                        return <option key={i} value={i} disabled={disabled}>{label}</option>;
                                    })}
                                </ThemedSelect>
                                <ThemedTimeSelect
                                    icon={(
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10" />
                                            <polyline points="12 6 12 12 16 14" />
                                        </svg>
                                    )}
                                    label="Time"
                                    value={selectedHour}
                                    minHour={classifyOffset(selectedDay) === 'today' ? firstValidTodayHour : undefined}
                                    onChange={(v) => setSelectedHour(v)}
                                />
                            </div>
                            {isPastSelection ? (
                                <p style={{ fontSize: 12, color: '#b91c1c', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px' }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    That time is in the past — pick a future slot.
                                </p>
                            ) : (
                                <p style={{ fontSize: 12, color: '#78716c', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#78716c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2z" />
                                    </svg>
                                    Will publish on <span style={{ fontWeight: 600, color: '#44403c' }}>{publishStr}</span> at <span style={{ fontWeight: 600, color: '#44403c' }}>{timeStr}</span>.
                                </p>
                            )}
                        </div>
                    </div>
                    {/* Col 3: Instagram preview */}
                    <div style={{ minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e', marginBottom: 10 }}>Preview</div>
                        <div style={{ background: '#fff', border: '1px solid #e7e5e4', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 12px -4px rgba(0,0,0,0.08)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
                                <div style={{ width: 28, height: 28, borderRadius: 99, background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', padding: 2, flexShrink: 0 }}>
                                    <div style={{ width: '100%', height: '100%', borderRadius: 99, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, overflow: 'hidden' }}>
                                        {avatarOk ? (
                                            <img src={accountAvatarUrl} alt={displayHandle} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : '📷'}
                                    </div>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {displayHandle}
                                    </div>
                                    <div style={{ fontSize: 10, color: '#a8a29e' }}>
                                        {accountDisplayName ? `${accountDisplayName} · ${publishStr}` : publishStr}
                                    </div>
                                </div>
                                <span style={{ color: '#a8a29e', letterSpacing: 2 }}>•••</span>
                            </div>
                            <div style={{ aspectRatio: '1', position: 'relative', background: '#f5f5f4' }}>
                                <PostFrameRender frame={selectedFrame} productImage={selectedMedia?.productImageUrl || selectedMedia?.url} productTitle={selectedMedia?.title} bundle={selectedMedia?.bundle} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 12px' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2" strokeLinecap="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></svg>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                                <div style={{ flex: 1 }} />
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2" strokeLinecap="round"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" /></svg>
                            </div>
                            <div style={{ padding: '0 12px 12px' }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>1,284 likes</div>
                                <div style={{ fontSize: 12, color: '#44403c', lineHeight: 1.5 }}>
                                    <span style={{ fontWeight: 600 }}>{displayHandle}</span>{' '}
                                    {caption || <span style={{ color: '#a8a29e', fontStyle: 'italic' }}>Your caption here…</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// --- StoryDayPicker ---
// Centered modal that lists all of a day's stories. User picks one to edit,
// hits "+" to add another, or deletes one inline.
function StoryDayPicker({ dayOffset, slides = [], weekStart, onPick, onAddNew, onDelete, onClose, accountHandle = 'your.handle', accountAvatarUrl = '' }) {
    const dayLabel = (() => {
        const d = addDays(weekStart, dayOffset);
        return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    })();
    return (
        <>
            <div onClick={onClose} style={{
                position: 'fixed', inset: 0, zIndex: 1200,
                background: 'rgba(28,25,23,0.45)', backdropFilter: 'blur(4px)',
                animation: 'igBackdropFade 220ms ease both',
            }} />
            <div style={{
                position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1201,
                width: 'min(640px, 92vw)', maxHeight: '82vh', overflow: 'auto',
                background: '#fff', borderRadius: 16,
                boxShadow: '0 24px 60px -12px rgba(0,0,0,0.35)',
                animation: 'igSheetUp 240ms cubic-bezier(.2,.8,.2,1) both',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid #f5f5f4' }}>
                    <div>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#a8a29e' }}>Stories</div>
                        <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1f2937', margin: 0 }}>{dayLabel}</h3>
                        <div style={{ fontSize: 11, color: '#78716c', marginTop: 2 }}>{slides.length} slide{slides.length !== 1 ? 's' : ''} on this day</div>
                    </div>
                    <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 99, border: '1px solid #e7e5e4', background: '#fff', color: '#44403c', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Close">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>

                {/* Slide grid */}
                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                    {slides.map((s) => {
                        const fr = STORY_FRAMES.find((f) => f.id === s.frameId) || STORY_FRAMES[0];
                        const failed = s.publishStatus === 'failed';
                        return (
                            <div key={s.id} style={{ position: 'relative' }}>
                                <button type="button" onClick={() => onPick?.(s)} title="Edit this story" style={{
                                    width: '100%', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer',
                                    display: 'block',
                                }}>
                                    <div style={{
                                        position: 'relative', borderRadius: 12, overflow: 'hidden',
                                        border: failed ? '2px solid #dc2626' : '1px solid rgba(214,211,209,0.7)',
                                        aspectRatio: '9 / 16', background: '#000',
                                    }}>
                                        <StoryFrameRender frame={fr} fields={s.fields} productImage={s.media?.productImageUrl || s.media?.url} productTitle={s.media?.title} />
                                        {/* IG progress + handle (matches phone tile) */}
                                        <div style={{ position: 'absolute', top: 6, left: 6, right: 6, display: 'flex', gap: 2, zIndex: 10, pointerEvents: 'none' }}>
                                            <div style={{ flex: 1, height: 2, borderRadius: 99, background: 'rgba(255,255,255,0.35)', overflow: 'hidden' }}>
                                                <div style={{ height: '100%', background: '#fff', width: '60%' }} />
                                            </div>
                                        </div>
                                        <div style={{ position: 'absolute', top: 14, left: 8, display: 'flex', alignItems: 'center', gap: 5, zIndex: 10, pointerEvents: 'none' }}>
                                            <div style={{ width: 16, height: 16, borderRadius: 99, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, overflow: 'hidden' }}>
                                                {accountAvatarUrl ? <img src={accountAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '📷'}
                                            </div>
                                            <span style={{ fontSize: 8, fontWeight: 600, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>{accountHandle}</span>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: 8, fontSize: 11, fontWeight: 600, color: '#1f2937', textAlign: 'left' }}>{fr.name}</div>
                                    <div style={{ fontSize: 10, color: failed ? '#dc2626' : '#78716c', textAlign: 'left' }}>
                                        {failed ? 'Publish failed — tap to retry' : `${formatHour(s.hour)}${s.publishStatus === 'scheduling' ? ' · Scheduling…' : ''}`}
                                    </div>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); onDelete?.(s); }} title="Delete this slide" style={{
                                    position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: 99,
                                    border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                </button>
                            </div>
                        );
                    })}
                    {/* Add-new tile */}
                    <button type="button" onClick={onAddNew} title="Add another story" style={{
                        aspectRatio: '9 / 16', borderRadius: 12, border: '2px dashed #d6d3d1',
                        background: 'rgba(245,245,244,0.6)', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        gap: 6, color: '#78716c', fontSize: 12, fontWeight: 600,
                    }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        Add story
                    </button>
                </div>
            </div>
        </>
    );
}

// --- StoryComposerSheet (full-screen) ---
// Mirrors ComposerSheet's UX but for 9:16 stories: pick a frame template,
// schedule the publish time, see a live phone preview. Save persists into
// the parent `stories[dayOffset]` map.
function StoryComposerSheet({
    story, media, weekStart, dayOffset, onClose, onSave, onDelete,
    accountHandle = '', accountDisplayName = '', accountAvatarUrl = '',
}) {
    const isEditing = !!story;
    const displayHandle = (accountHandle || '').trim() || 'your.handle';

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const currentHour = now.getHours() + now.getMinutes() / 60;
    // 1-minute buffer past "now" — see post composer for rationale.
    const firstValidTodayHour = (() => {
        const next = currentHour + 1 / 60;
        return next < 24 ? next : undefined;
    })();
    const todayOffsetInWindow = (() => {
        for (let i = 0; i < 7; i++) {
            if (addDays(weekStart, i).toDateString() === now.toDateString()) return i;
        }
        return -1;
    })();
    const classifyOffset = (i) => {
        const d = addDays(weekStart, i);
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        if (dayStart < todayStart) return 'past';
        if (dayStart.getTime() === todayStart.getTime()) return 'today';
        return 'future';
    };
    const todayHasValidSlot = firstValidTodayHour !== undefined;
    const defaultDayOffset = (() => {
        if (dayOffset != null) return dayOffset;
        if (todayOffsetInWindow >= 0 && todayHasValidSlot) return todayOffsetInWindow;
        for (let i = 0; i < 7; i++) if (classifyOffset(i) === 'future') return i;
        return Math.max(0, todayOffsetInWindow);
    })();
    const defaultHour = (() => {
        const cls = classifyOffset(defaultDayOffset);
        if (cls === 'today') return firstValidTodayHour ?? 10;
        return 10; // 10:00 AM
    })();

    const [selectedFrameId, setSelectedFrameId] = useState(story?.frameId || 'splash');
    const selectedFrame = STORY_FRAMES.find((f) => f.id === selectedFrameId) || STORY_FRAMES[0];
    const [fields, setFields] = useState(() => ({ ...selectedFrame.defaults, ...(story?.fields || {}) }));
    const [selectedDay, setSelectedDay] = useState(story?.dayOffset ?? defaultDayOffset);
    const [selectedHour, setSelectedHour] = useState(story?.hour ?? defaultHour);
    const [caption, setCaption] = useState(story?.caption || '');
    const [closing, setClosing] = useState(false);
    const pendingActionRef = useRef(null);
    const requestClose = (action) => {
        if (closing) return;
        pendingActionRef.current = typeof action === 'function' ? action : null;
        setClosing(true);
    };
    const handleExitEnd = (e) => {
        if (e && e.target !== e.currentTarget) return;
        if (!closing) return;
        const action = pendingActionRef.current;
        pendingActionRef.current = null;
        if (action) action(); else onClose?.();
    };

    // When user picks a different frame, seed its defaults but preserve any
    // overlapping fields (urgency/cta) so they don't have to retype.
    const switchFrame = (id) => {
        setSelectedFrameId(id);
        const f = STORY_FRAMES.find((fr) => fr.id === id);
        setFields((cur) => ({ ...(f?.defaults || {}), ...cur }));
    };
    const updateField = (k, v) => setFields((cur) => ({ ...cur, [k]: v }));

    // Auto-bump hour when switching to today and the saved hour is now in the
    // past. Skip the initial mount so editing a story keeps showing the time
    // the user originally picked (the red banner flags past selections).
    const dayChangedRef = useRef(false);
    useEffect(() => {
        if (!dayChangedRef.current) { dayChangedRef.current = true; return; }
        const cls = classifyOffset(selectedDay);
        if (cls === 'today' && firstValidTodayHour !== undefined && selectedHour < firstValidTodayHour) {
            setSelectedHour(firstValidTodayHour);
        }
    }, [selectedDay]); // eslint-disable-line react-hooks/exhaustive-deps

    const publishDate = addDays(weekStart, selectedDay);
    const publishStr = publishDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    const timeStr = formatHour(selectedHour);
    const publishDateTime = (() => { const d = new Date(publishDate); const [hh, mm] = hourToHM(selectedHour); d.setHours(hh, mm, 0, 0); return d; })();
    const isPastSelection = publishDateTime <= now;

    // Prefer the original product URL over the composite — the composite has
    // the previously-baked frame in it, so re-rendering the React overlay on
    // top would double-frame the preview.
    const productImage = media?.productImageUrl || media?.url || story?.media?.productImageUrl || story?.media?.url || null;
    const productTitle = media?.title || story?.media?.title || '';

    return (
        <>
            <div onClick={() => requestClose()} style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(28,25,23,0.35)', backdropFilter: 'blur(3px)',
                animation: closing ? 'igBackdropFadeOut 300ms ease both' : 'igBackdropFade 280ms ease both',
            }} />
            <div onAnimationEnd={handleExitEnd} style={{
                position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, zIndex: 1001,
                background: '#fff',
                animation: closing ? 'igSheetDown 360ms cubic-bezier(.4,0,.8,.2) both' : 'igSheetUp 400ms cubic-bezier(.2,.8,.2,1) both',
                display: 'flex', flexDirection: 'column',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #f5f5f4' }}>
                    <div>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#a8a29e', marginBottom: 2 }}>{isEditing ? 'Edit story' : 'Compose story'}</div>
                        <h3 style={{ fontSize: 22, fontWeight: 600, color: '#1f2937', margin: 0, letterSpacing: '-0.02em' }}>{isEditing ? 'Edit story' : 'New story'}</h3>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {isEditing && (
                            <button onClick={() => requestClose(() => onDelete?.(dayOffset))} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                                Delete
                            </button>
                        )}
                        <button onClick={() => requestClose()} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e7e5e4', background: '#fff', color: '#44403c', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                        <button
                            onClick={() => {
                                if (isPastSelection) { toast.error('Pick a future time for this story.'); return; }
                                requestClose(() => onSave?.({
                                    dayOffset: selectedDay, hour: selectedHour,
                                    frameId: selectedFrameId, fields,
                                    caption,
                                    media: media || story?.media || null,
                                }));
                            }}
                            disabled={isPastSelection}
                            style={{
                                padding: '8px 18px', borderRadius: 8,
                                border: '1px solid ' + (isPastSelection ? '#d6d3d1' : '#1f2937'),
                                background: isPastSelection ? '#e7e5e4' : '#1f2937',
                                color: isPastSelection ? '#a8a29e' : '#fff',
                                fontSize: 13, fontWeight: 600,
                                cursor: isPastSelection ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {isEditing ? 'Save changes' : 'Schedule story'}
                        </button>
                    </div>
                </div>

                {/* Body — frames | fields | preview */}
                <div style={{ flex: 1, overflow: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr 320px', gap: 24, padding: 24 }}>
                    {/* Frames */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e' }}>Story Frames</div>
                            <span style={{ fontSize: 10, color: '#a8a29e' }}>{selectedFrame.name}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, maxWidth: 360 }}>
                            {STORY_FRAMES.map((fr) => {
                                const isSel = fr.id === selectedFrameId;
                                return (
                                    <button key={fr.id} type="button" onClick={() => switchFrame(fr.id)} title={fr.name} style={{
                                        position: 'relative', padding: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                                        aspectRatio: '9 / 16',
                                        border: isSel ? '2px solid #111827' : '1px solid rgba(214,211,209,0.7)',
                                        outline: isSel ? '2px solid #111827' : 'none', outlineOffset: 2,
                                        transform: isSel ? 'scale(0.97)' : 'none', transition: 'all 150ms ease',
                                        background: '#fff',
                                    }}>
                                        <StoryFrameRender frame={fr} fields={{ ...fr.defaults, ...fields }} productImage={productImage} productTitle={productTitle} />
                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '10px 4px 3px', background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.75))', color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'center', pointerEvents: 'none' }}>{fr.name}</div>
                                        {isSel && (
                                            <div style={{ position: 'absolute', top: 3, right: 3, width: 14, height: 14, borderRadius: 99, background: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                                                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Fields + Schedule */}
                    <div>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e', marginBottom: 10 }}>Frame content</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            {selectedFrame.style === 'storybogo' ? (
                                <>
                                    <FieldRow label="Buy">
                                        <input type="number" min="1" value={fields.buyQty ?? 1} onChange={(e) => updateField('buyQty', parseInt(e.target.value, 10) || 1)} style={composerInputCls} />
                                    </FieldRow>
                                    <FieldRow label="Get free">
                                        <input type="number" min="1" value={fields.getQty ?? 1} onChange={(e) => updateField('getQty', parseInt(e.target.value, 10) || 1)} style={composerInputCls} />
                                    </FieldRow>
                                </>
                            ) : selectedFrame.style === 'splash' || selectedFrame.style === 'midnight' ? (
                                <FieldRow label="Discount %">
                                    <input type="number" min="1" max="99" value={fields.discountPct ?? 0} onChange={(e) => updateField('discountPct', parseInt(e.target.value, 10) || 0)} style={composerInputCls} />
                                </FieldRow>
                            ) : null}
                            {(selectedFrame.style === 'midnight' || selectedFrame.style === 'storysoon') && (
                                <FieldRow label="Headline">
                                    <input type="text" value={fields.headline ?? ''} onChange={(e) => updateField('headline', e.target.value)} style={composerInputCls} />
                                </FieldRow>
                            )}
                            <FieldRow label="Urgency text">
                                <input type="text" value={fields.urgency ?? ''} onChange={(e) => updateField('urgency', e.target.value)} style={composerInputCls} placeholder="24 hours only" />
                            </FieldRow>
                            <FieldRow label="Call to action">
                                <input type="text" value={fields.cta ?? ''} onChange={(e) => updateField('cta', e.target.value)} style={composerInputCls} placeholder="SHOP NOW" />
                            </FieldRow>
                        </div>

                        <div style={{ marginTop: 22 }}>
                            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e', marginBottom: 10 }}>Caption</div>
                            <textarea
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                placeholder="Write something lovely…"
                                rows={4}
                                style={{
                                    width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #e7e5e4',
                                    fontSize: 13, color: '#1f2937', resize: 'none', outline: 'none', fontFamily: 'inherit',
                                    lineHeight: 1.5, boxSizing: 'border-box',
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#1f2937'}
                                onBlur={(e) => e.target.style.borderColor = '#e7e5e4'}
                            />
                            <div style={{ textAlign: 'right', fontSize: 11, color: '#a8a29e', marginTop: 4 }}>{caption.length} / 2200</div>
                        </div>

                        <div style={{ marginTop: 22 }}>
                            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e', marginBottom: 10 }}>Schedule</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 10 }}>
                                <ThemedSelect
                                    icon={(<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>)}
                                    label="Day" value={selectedDay} onChange={(v) => setSelectedDay(Number(v))}>
                                    {Array.from({ length: 7 }).map((_, i) => {
                                        const d = addDays(weekStart, i);
                                        const cls = classifyOffset(i);
                                        const disabled = cls === 'past' || (cls === 'today' && !todayHasValidSlot);
                                        return <option key={i} value={i} disabled={disabled}>{`${DAY_LABELS[d.getDay()]} ${d.getDate()}`}</option>;
                                    })}
                                </ThemedSelect>
                                <ThemedTimeSelect
                                    icon={(<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>)}
                                    label="Time"
                                    value={selectedHour}
                                    minHour={classifyOffset(selectedDay) === 'today' ? firstValidTodayHour : undefined}
                                    onChange={(v) => setSelectedHour(v)}
                                />
                            </div>
                            <p style={{ fontSize: 12, color: isPastSelection ? '#b91c1c' : '#78716c', marginTop: 10 }}>
                                {isPastSelection
                                    ? 'That time is in the past — pick a future slot.'
                                    : <>Will publish on <strong>{publishStr}</strong> at <strong>{timeStr}</strong> · expires 24h later.</>}
                            </p>
                        </div>
                    </div>

                    {/* Phone preview */}
                    <div>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#a8a29e', marginBottom: 10 }}>Preview</div>
                        <div style={{ background: '#1f2937', borderRadius: 26, padding: 8, boxShadow: '0 18px 40px -14px rgba(0,0,0,0.45)' }}>
                            <div style={{ background: '#000', borderRadius: 20, overflow: 'hidden', position: 'relative', aspectRatio: '9 / 16' }}>
                                <StoryFrameRender frame={selectedFrame} fields={fields} productImage={productImage} productTitle={productTitle} />
                                <div style={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', gap: 3, zIndex: 10, pointerEvents: 'none' }}>
                                    <div style={{ flex: 1, height: 2, borderRadius: 99, background: 'rgba(255,255,255,0.35)', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', background: '#fff', width: '60%' }} />
                                    </div>
                                </div>
                                <div style={{ position: 'absolute', top: 22, left: 12, display: 'flex', alignItems: 'center', gap: 6, zIndex: 10, pointerEvents: 'none' }}>
                                    <div style={{ width: 22, height: 22, borderRadius: 99, background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, overflow: 'hidden' }}>
                                        {accountAvatarUrl ? <img src={accountAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '📷'}
                                    </div>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>{displayHandle}</span>
                                </div>
                            </div>
                        </div>
                        <p style={{ fontSize: 11, color: '#78716c', marginTop: 10, lineHeight: 1.5 }}>
                            Stories disappear after 24 hours. Preview is approximate — fonts and stickers may render slightly differently on Instagram.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}
const composerInputCls = { width: '100%', fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid #e7e5e4', background: '#fff', outline: 'none', color: '#1f2937', boxSizing: 'border-box' };
function FieldRow({ label, children }) {
    return (
        <label style={{ display: 'block' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#78716c', textTransform: 'uppercase', letterSpacing: '0.12em', display: 'block', marginBottom: 4 }}>{label}</span>
            {children}
        </label>
    );
}

// --- CalendarToolbar ---
function CalendarToolbar({ weekStart, goPrev, goNext, goToday, calendarMode, setCalendarMode }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid #f5f5f4', background: 'rgba(255,255,255,0.7)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <button onClick={goToday} style={{ background: 'none', border: 'none', fontSize: 13, fontWeight: 500, color: '#111827', cursor: 'pointer', padding: 0 }}>Today</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button onClick={goPrev} style={iconBtnStyle}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg></button>
                    <button onClick={goNext} style={iconBtnStyle}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg></button>
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937', letterSpacing: '-0.01em', marginLeft: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatRange(weekStart)}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: '#a8a29e' }}>Pacific Time</span>
                <div style={{ display: 'flex', alignItems: 'center', background: '#f5f5f4', borderRadius: 99, padding: 3, position: 'relative' }}>
                    {['feed', 'stories'].map((k) => (
                        <button key={k} onClick={() => setCalendarMode(k)} style={{
                            position: 'relative', zIndex: 2, flex: 1, padding: '6px 16px', fontSize: 12, fontWeight: 600,
                            borderRadius: 99, border: 'none', background: 'transparent', color: calendarMode === k ? '#fff' : '#78716c', cursor: 'pointer', transition: 'color 200ms',
                        }}>{k === 'feed' ? 'Feed' : 'Stories'}</button>
                    ))}
                    <div style={{ position: 'absolute', top: 3, bottom: 3, borderRadius: 99, background: '#1f2937', transition: 'all 300ms ease', left: calendarMode === 'feed' ? 3 : '50%', width: 'calc(50% - 3px)', boxShadow: '0 4px 10px -4px rgba(0,0,0,0.3)' }} />
                </div>
            </div>
        </div>
    );
}
const iconBtnStyle = { width: 32, height: 32, borderRadius: 99, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#57534e', cursor: 'pointer' };

// ============================================================================
// Main
// ============================================================================
// --- ConnectModal (Notion Calendar style) ---
function ConnectModal({ onConnect, onClose, locked = false, busy = false }) {
    return (
        <>
            <div onClick={locked ? undefined : onClose} style={{
                position: 'absolute', inset: 0, zIndex: 40,
                background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(3px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }} />
            <div style={{
                position: 'absolute', top: -250, left: 0, right: 0, bottom: 0, zIndex: 50,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
            }}><div style={{
                pointerEvents: 'auto',
                width: 440, background: '#fff', borderRadius: 16,
                boxShadow: '0 24px 60px -12px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
                overflow: 'hidden',
            }}>
                {/* Preview area */}
                <div style={{
                    height: 200, position: 'relative', overflow: 'hidden',
                    background: 'linear-gradient(135deg, #fce7f3 0%, #fef3c7 50%, #e0e7ff 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        width: 280, height: 160, background: '#fff', borderRadius: 12,
                        boxShadow: '0 20px 40px -12px rgba(0,0,0,0.15)',
                        padding: 16, display: 'flex', flexDirection: 'column',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <div style={{ width: 24, height: 24, borderRadius: 6, background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                                </svg>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#1f2937' }}>Social Scheduler</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, flex: 1 }}>
                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                                <div key={i} style={{ fontSize: 7, color: '#a8a29e', textAlign: 'center', fontWeight: 600 }}>{d}</div>
                            ))}
                            {Array.from({ length: 14 }).map((_, i) => (
                                <div key={i} style={{
                                    borderRadius: 4, background: [2, 5, 8, 11].includes(i) ? 'linear-gradient(135deg, #fecdd3, #fde68a)' : '#f5f5f4',
                                    aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 6, color: '#78716c',
                                }}>
                                    {[2, 5, 8, 11].includes(i) ? ['☕', '🌅', '🌸', '📚'][Math.floor(Math.random() * 4)] : ''}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '28px 32px 32px', textAlign: 'center' }}>
                    <h2 style={{ fontSize: 22, fontWeight: 600, color: '#1f2937', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
                        Connect Instagram
                    </h2>
                    <p style={{ fontSize: 14, color: '#78716c', margin: '0 0 24px', lineHeight: 1.6 }}>
                        Connect your Instagram account to view and reply to messages from your audience.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <button onClick={onConnect} disabled={busy} style={{
                            width: '100%', padding: '12px 20px', borderRadius: 10, border: 'none',
                            fontSize: 14, fontWeight: 600, color: '#fff', cursor: busy ? 'not-allowed' : 'pointer',
                            background: '#111827', boxShadow: '0 4px 12px -4px rgba(0,0,0,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            transition: 'transform 0.15s', opacity: busy ? 0.7 : 1,
                        }}
                            onMouseEnter={(e) => { if (!busy) e.currentTarget.style.transform = 'scale(1.02)'; }}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            {busy ? (
                                <>
                                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'igSpin 0.6s linear infinite' }} />
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                                    </svg>
                                    Continue with Instagram
                                </>
                            )}
                        </button>
                        {!locked && (
                            <button onClick={onClose} style={{
                                width: '100%', padding: '10px 20px', borderRadius: 10,
                                border: '1px solid #e7e5e4', background: '#fff',
                                fontSize: 13, fontWeight: 500, color: '#78716c', cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f4'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                            >
                                Maybe later
                            </button>
                        )}
                    </div>
                    <p style={{ fontSize: 11, color: '#a8a29e', margin: '16px 0 0', lineHeight: 1.4 }}>
                        We'll only request the permissions we need to publish on your behalf.
                    </p>
                </div>
            </div></div>
        </>
    );
}

export default function InstagramHome({
    instagramConnected = false,
    instagramHandle = '',
    instagramDisplayName = '',
    instagramAvatarUrl = '',
    onInstagramConnect,
    instagramBusy = false,
    instagramLoading = false,
}) {
    const { tokens } = useAuth();
    const showConnectModal = !instagramLoading && !instagramConnected;
    const [weekStart, setWeekStart] = useState(() => centeredWeekStart());
    // Scheduled posts load from /api/social-posts on mount + week change.
    const [scheduled, setScheduled] = useState([]);
    const [scheduledLoading, setScheduledLoading] = useState(false);
    // Stories: { [dayOffset]: [{ id, dayOffset, hour, frameId, fields, caption, media, serverId, publishStatus }, ...] }
    // Multiple slides per day. Migrates the old single-object shape on first read.
    const STORIES_KEY = 'ig_stories_v1';
    const [stories, setStories] = useState(() => {
        try {
            const raw = JSON.parse(localStorage.getItem(STORIES_KEY) || '{}');
            const out = {};
            for (const [day, value] of Object.entries(raw)) {
                if (Array.isArray(value)) {
                    out[day] = value;
                } else if (value && typeof value === 'object' && Object.keys(value).length > 0) {
                    out[day] = [{ id: value.id || `s-${day}-${Date.now()}`, ...value }];
                }
            }
            return out;
        } catch { return {}; }
    });
    useEffect(() => {
        try { localStorage.setItem(STORIES_KEY, JSON.stringify(stories)); } catch {}
    }, [stories]);
    const [storyComposerOpen, setStoryComposerOpen] = useState(false);
    const [storyComposerMedia, setStoryComposerMedia] = useState(null);
    const [storyComposerDay, setStoryComposerDay] = useState(null);
    const [editingStory, setEditingStory] = useState(null);
    const [calendarMode, setCalendarMode] = useState('feed');
    const [draggingMedia, setDraggingMedia] = useState(null);
    const [dragOverCell, setDragOverCell] = useState(null);
    const [justDroppedId, setJustDroppedId] = useState(null);
    const [composerOpen, setComposerOpen] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [composerMedia, setComposerMedia] = useState(null);
    // When the user drags onto a calendar cell we pre-seed the composer with
    // that cell's day/hour instead of saving immediately — they confirm in the
    // modal first.
    const [composerInitialDay, setComposerInitialDay] = useState(null);
    const [composerInitialHour, setComposerInitialHour] = useState(null);

    // Real server-backed library state + infinite-scroll pagination.
    const LIBRARY_PAGE_SIZE = 24;
    const [libraryItems, setLibraryItems] = useState([]);
    const [libraryLoading, setLibraryLoading] = useState(false);     // initial load
    const [libraryLoadingMore, setLibraryLoadingMore] = useState(false); // appending
    const [libraryHasMore, setLibraryHasMore] = useState(false);
    const libraryOffsetRef = useRef(0);
    const libraryInFlightRef = useRef(false);
    const [uploading, setUploading] = useState(false);

    const loadLibraryPage = useCallback(async ({ reset = false } = {}) => {
        if (!tokens?.accessToken) return;
        if (libraryInFlightRef.current) return;
        libraryInFlightRef.current = true;
        const offset = reset ? 0 : libraryOffsetRef.current;
        if (reset) setLibraryLoading(true); else setLibraryLoadingMore(true);
        const res = await listLibraryItems({
            accessToken: tokens.accessToken,
            limit: LIBRARY_PAGE_SIZE,
            offset,
        });
        if (reset) setLibraryLoading(false); else setLibraryLoadingMore(false);
        libraryInFlightRef.current = false;
        if (!res.ok) {
            if (reset) toast.error(res.error || 'Failed to load library');
            return;
        }
        const items = (res.data?.items || []).map(normalizeLibraryItem);
        const total = typeof res.data?.total === 'number' ? res.data.total : items.length;
        setLibraryItems((prev) => {
            const next = reset ? items : [...prev, ...items];
            libraryOffsetRef.current = next.length;
            setLibraryHasMore(next.length < total);
            return next;
        });
    }, [tokens?.accessToken]);

    // Initial load + reload when the user's auth token changes.
    useEffect(() => { loadLibraryPage({ reset: true }); }, [loadLibraryPage]);

    /** Fetch scheduled/publishing/published/failed posts from the backend and
     *  map them into the calendar state. We refetch whenever the visible
     *  `weekStart` changes so out-of-week posts don't clutter state. */
    const loadScheduledPosts = useCallback(async () => {
        if (!tokens?.accessToken) return;
        setScheduledLoading(true);
        // Fetch a 3-week window centered on the current weekStart — covers
        // prev/next nav without refetching every click.
        const from = addDays(weekStart, -7);
        const to = addDays(weekStart, 14);
        const res = await listSocialPosts({
            accessToken: tokens.accessToken,
            platform: 'instagram',
            from: from.toISOString(),
            to: to.toISOString(),
        });
        setScheduledLoading(false);
        if (!res.ok) {
            toast.error(res.error || 'Failed to load scheduled posts');
            return;
        }
        // Stories share the same /api/social-posts endpoint but live in their
        // own tab — keep them out of the feed grid so they don't render as
        // feed cards. Posts without an explicit `kind` default to 'feed'.
        const rows = (res.data?.posts || [])
            .filter((p) => (p.kind || 'feed') === 'feed')
            .map((p) => serverPostToLocal(p, weekStart));
        setScheduled(rows);
    }, [tokens?.accessToken, weekStart]);

    useEffect(() => { loadScheduledPosts(); }, [loadScheduledPosts]);

    const refreshLibrary = useCallback(() => loadLibraryPage({ reset: true }), [loadLibraryPage]);
    const loadMoreLibrary = useCallback(() => {
        if (libraryLoadingMore || !libraryHasMore) return;
        loadLibraryPage({ reset: false });
    }, [loadLibraryPage, libraryLoadingMore, libraryHasMore]);

    const handleUploadFiles = async (files) => {
        if (!tokens?.accessToken) return toast.error('Not authenticated');
        setUploading(true);
        const added = [];
        for (const file of files) {
            const isVideo = file.type.startsWith('video/');
            const res = isVideo
                ? await uploadVideoToLibrary({ file, accessToken: tokens.accessToken })
                : await uploadImageToLibrary({ file, accessToken: tokens.accessToken });
            if (res.ok) {
                added.push(normalizeLibraryItem(res.data));
            } else {
                toast.error(`${file.name}: ${res.error || 'Upload failed'}`);
            }
        }
        if (added.length) {
            setLibraryItems((prev) => [...added, ...prev]);
            // New rows land at the top of the server list; bump the pagination
            // cursor by the same amount so the next "load more" doesn't return
            // duplicates of items we already have.
            libraryOffsetRef.current += added.length;
        }
        setUploading(false);
    };

    const handleDeleteLibraryItem = async (item) => {
        if (!item.serverId || !tokens?.accessToken) return;
        // Optimistic remove — put it back on failure.
        setLibraryItems((prev) => prev.filter((m) => m.id !== item.id));
        libraryOffsetRef.current = Math.max(0, libraryOffsetRef.current - 1);
        const res = await apiDeleteLibraryItem({ id: item.serverId, accessToken: tokens.accessToken });
        if (!res.ok) {
            toast.error(res.error || 'Failed to delete');
            refreshLibrary();
        }
    };

    /**
     * Add a GIF from the Giphy tab into the user's library. We store the raw
     * Giphy CDN URL (source: 'giphy') — no Cloudflare round-trip. The item
     * shows up in the My Media tab so it can be dragged onto the calendar
     * just like an upload.
     */
    const handleAddGiphyToLibrary = async (gif) => {
        if (!tokens?.accessToken) return toast.error('Not authenticated');
        const res = await createLibraryItem({
            type: 'image',
            source: 'giphy',
            url: gif.url,
            thumbnailUrl: gif.previewUrl || null,
            title: gif.title,
            mimeType: 'image/gif',
            width: gif.width ?? null,
            height: gif.height ?? null,
            accessToken: tokens.accessToken,
        });
        if (res.ok) {
            setLibraryItems((prev) => [normalizeLibraryItem(res.data), ...prev]);
            libraryOffsetRef.current += 1;
            toast.success('Added to library');
        } else {
            toast.error(res.error || 'Failed to save GIF');
        }
    };

    const mediaMap = useMemo(() => {
        const m = new Map();
        libraryItems.forEach((s) => m.set(s.id, s));
        return m;
    }, [libraryItems]);

    const goPrev = () => setWeekStart((w) => addDays(w, -7));
    const goNext = () => setWeekStart((w) => addDays(w, 7));
    const goToday = () => setWeekStart(centeredWeekStart());

    const onMediaDragStart = (e, media) => { setDraggingMedia(media); e.dataTransfer.effectAllowed = 'copy'; try { e.dataTransfer.setData('text/plain', media.id); } catch {} };
    const onMediaDragEnd = () => { setDraggingMedia(null); setDragOverCell(null); };
    const onCellDragOver = (e, dayOffset, hour) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOverCell(`${dayOffset}-${hour}`); };
    const onCellDrop = (e, dayOffset, hour) => {
        e.preventDefault();
        if (!draggingMedia) return;
        const media = draggingMedia;
        setDraggingMedia(null);
        setDragOverCell(null);
        // Open the composer pre-filled with the dragged item + dropped slot.
        // Past-slot validation is left to the composer (Save button is disabled
        // until the user picks a future day/time), matching the story flow —
        // dropping is never blocked, the modal always opens.
        setComposerMedia(media);
        setEditingPost(null);
        setComposerInitialDay(dayOffset);
        setComposerInitialHour(hour);
        setComposerOpen(true);
    };
    // --- Story handlers ---
    const newStoryId = () => `s${Date.now()}${Math.random().toString(36).slice(2, 7)}`;
    const findStoryById = (id) => {
        for (const list of Object.values(stories)) {
            for (const s of list || []) if (s.id === id) return s;
        }
        return null;
    };
    // Drop always opens a fresh new-story composer with the dragged product.
    const onStoryDrop = (dayOffset, media) => {
        setStoryComposerMedia(media);
        setStoryComposerDay(dayOffset);
        setEditingStory(null);
        setStoryComposerOpen(true);
    };
    // Open composer to add a brand-new slide on this day.
    const onAddStory = (dayOffset) => {
        setEditingStory(null);
        setStoryComposerMedia(null);
        setStoryComposerDay(dayOffset);
        setStoryComposerOpen(true);
    };
    // Open composer to edit a specific slide (passed from the phone slider).
    const onPickStory = (slide) => {
        setEditingStory(slide);
        setStoryComposerMedia(slide.media || null);
        setStoryComposerDay(slide.dayOffset);
        setStoryComposerOpen(true);
    };
    const closeStoryComposer = () => {
        setStoryComposerOpen(false);
        setEditingStory(null);
        setStoryComposerMedia(null);
        setStoryComposerDay(null);
    };
    const saveStory = async (data) => {
        const newDay = data.dayOffset;
        const editingId = editingStory?.id;
        const existing = editingId ? findStoryById(editingId) : null;
        const id = existing?.id || newStoryId();
        const oldDay = existing?.dayOffset;
        // Optimistic local update first so the phone tile reflects the choice
        // immediately, even if the upload + publish step takes a few seconds.
        const optimistic = {
            id,
            dayOffset: newDay,
            hour: data.hour,
            frameId: data.frameId,
            fields: data.fields,
            caption: data.caption || '',
            media: data.media,
            serverId: existing?.serverId || null,
            publishStatus: 'scheduling',
        };
        setStories((prev) => {
            const next = { ...prev };
            // If the user moved the story to a different day, pull it out of the old list.
            if (oldDay !== undefined && oldDay !== newDay) {
                next[oldDay] = (next[oldDay] || []).filter((s) => s.id !== id);
                if (!next[oldDay].length) delete next[oldDay];
            }
            const list = next[newDay] || [];
            const idx = list.findIndex((s) => s.id === id);
            next[newDay] = idx >= 0 ? list.map((s) => (s.id === id ? optimistic : s)) : [...list, optimistic];
            return next;
        });

        if (!tokens?.accessToken) {
            toast.error('Not authenticated — story saved locally only.');
            return;
        }

        const sourceImageUrl = data.media?.productImageUrl || data.media?.url;
        if (!sourceImageUrl) {
            toast.error('No product image — story saved locally only.');
            return;
        }

        try {
            // 1. Composite the framed 9:16 JPEG.
            const frameDef = STORY_FRAMES.find((f) => f.id === data.frameId);
            let mediaArr;
            if (frameDef && frameDef.style !== 'none') {
                const blob = await renderStoryFramedImageToBlob({
                    frame: frameDef,
                    productImageUrl: sourceImageUrl,
                    fields: data.fields,
                });
                if (!blob) throw new Error('Composite returned no blob');
                const file = new File([blob], `story-${Date.now()}.jpg`, { type: 'image/jpeg' });
                // 2. Upload to Cloudflare via the existing media-library endpoint.
                const upRes = await uploadImageToLibrary({ file, accessToken: tokens.accessToken });
                if (!upRes.ok || !(upRes.data?.url || upRes.data?.cloudflareId)) {
                    throw new Error(upRes.error || 'Upload failed');
                }
                mediaArr = [{
                    url: upRes.data.url || sourceImageUrl,
                    type: 'image',
                    id: upRes.data.cloudflareId || undefined,
                    uid: upRes.data.cloudflareUid || undefined,
                    productId: data.media?.product?.id || data.media?.product?.productId || undefined,
                    productTitle: data.media?.product?.title || data.media?.title || undefined,
                    productImageUrl: sourceImageUrl,
                }];
            } else {
                // No frame — post the raw product image as the story.
                mediaArr = [{
                    url: sourceImageUrl,
                    type: 'image',
                    productId: data.media?.product?.id || data.media?.product?.productId || undefined,
                    productTitle: data.media?.product?.title || data.media?.title || undefined,
                    productImageUrl: sourceImageUrl,
                }];
            }

            // 3. Build scheduledAt + publish (PUT if updating existing, else POST).
            const date = addDays(weekStart, newDay);
            const [sh, sm] = hourToHM(data.hour);
            date.setHours(sh, sm, 0, 0);
            const payload = {
                accessToken: tokens.accessToken,
                platform: 'instagram',
                status: 'scheduled',
                kind: 'story',
                caption: data.caption || '',
                media: mediaArr,
                scheduledAt: date.toISOString(),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
            const res = existing?.serverId
                ? await updateSocialPost({ id: existing.serverId, ...payload })
                : await createSocialPost(payload);
            if (!res.ok) throw new Error(res.error || 'Publish failed');

            // 4. Mark as scheduled with serverId so we can later edit/delete.
            const serverPost = res.data?.post;
            setStories((prev) => {
                const list = prev[newDay] || [];
                return {
                    ...prev,
                    [newDay]: list.map((s) => (s.id === id ? {
                        ...s,
                        serverId: serverPost?.id || s.serverId || null,
                        publishStatus: 'scheduled',
                        media: mediaArr[0],
                    } : s)),
                };
            });
            toast.success(existing ? 'Story updated' : 'Story scheduled to Instagram');
        } catch (err) {
            console.warn('Story publish failed:', err);
            // Roll the publish state back but keep the local draft so the user
            // doesn't lose their work.
            setStories((prev) => {
                const list = prev[newDay] || [];
                return {
                    ...prev,
                    [newDay]: list.map((s) => (s.id === id ? { ...s, publishStatus: 'failed', publishError: err.message || String(err) } : s)),
                };
            });
            toast.error('Couldn’t publish to Instagram — saved locally as a draft.');
        }
    };
    const deleteStory = async (dayOffset, storyId) => {
        const target = (stories[dayOffset] || []).find((s) => s.id === storyId);
        setStories((prev) => {
            const list = (prev[dayOffset] || []).filter((s) => s.id !== storyId);
            const next = { ...prev };
            if (list.length) next[dayOffset] = list;
            else delete next[dayOffset];
            return next;
        });
        // Best-effort: also remove from the backend if it was published-scheduled.
        if (target?.serverId && tokens?.accessToken) {
            const res = await deleteSocialPost({ id: target.serverId, accessToken: tokens.accessToken });
            if (!res.ok) toast.error(res.error || 'Removed locally — backend delete failed');
            else toast.success('Story removed');
        } else {
            toast.success('Story removed');
        }
    };
    /** Delete a post. If it's server-backed, hit DELETE first (optimistic). */
    const deletePost = async (id) => {
        const existing = scheduled.find((p) => p.id === id);
        // Optimistic local remove.
        setScheduled((prev) => prev.filter((p) => p.id !== id));
        setEditingPost(null);
        setComposerOpen(false);
        if (existing?.serverId && tokens?.accessToken) {
            const res = await deleteSocialPost({ id: existing.serverId, accessToken: tokens.accessToken });
            if (!res.ok) {
                toast.error(res.error || 'Failed to delete post');
                loadScheduledPosts(); // rehydrate from server
            }
        }
    };

    /** Save from the composer. Builds a real `scheduledAt` ISO (UTC), resolves
     *  the selected library item into the `media[]` shape the publisher expects,
     *  and hits POST (new) or PUT (edit) with optimistic UI updates. */
    const savePost = async (data) => {
        setEditingPost(null);
        setComposerOpen(false);
        setComposerMedia(null);

        if (!tokens?.accessToken) { toast.error('Not authenticated'); return; }

        // Resolve the library item selected in the composer → publisher-shaped media row.
        // For drag-from-products, the item isn't in mediaMap; fall back to the
        // dragged object directly (it already has url + type).
        const libItem = mediaMap.get(data.mediaId) || data.draggedMedia || null;
        // The ORIGINAL product image — never the previously-framed one.
        // Server posts cache `productImageUrl` so re-editing doesn't double-frame.
        const sourceImageUrl = libItem?.productImageUrl || libItem?.url || null;
        let mediaArr = libItem ? [{
            url: libItem.url,
            type: libItem.type || 'image',
            id: libItem.cloudflareId || undefined,
            uid: libItem.cloudflareUid || undefined,
            embedUrl: libItem.embedUrl || undefined,
            productId: libItem.product?.id || libItem.product?.productId || undefined,
            productTitle: libItem.product?.title || libItem.title || undefined,
            productImageUrl: sourceImageUrl || undefined,
        }] : [];

        // For bundle media OR when a frame is selected, composite the actual
        // image (collage layout for bundles, frame overlay for products) into a
        // real JPEG, upload to Cloudflare, then post that URL instead. Falls
        // back to the raw image on any failure (CORS, network, etc.) so we
        // never block scheduling — just warn the user.
        const isBundleMedia = !!libItem?.isBundle && !!libItem?.bundle;
        const hasFrame = data.frameId && data.frameId !== 'none';
        if ((isBundleMedia || hasFrame) && mediaArr[0]) {
            const frameDef = hasFrame ? POST_FRAMES.find((fr) => fr.id === data.frameId) : null;
            const canBake = isBundleMedia || (hasFrame && !!frameDef && !!sourceImageUrl);
            if (canBake) {
                try {
                    const blob = await renderFramedImageToBlob({
                        frame: frameDef,
                        productImageUrl: sourceImageUrl,
                        bundle: isBundleMedia ? libItem.bundle : null,
                    });
                    if (blob) {
                        const namePrefix = isBundleMedia ? 'bundle' : 'framed';
                        const file = new File([blob], `${namePrefix}-${Date.now()}.jpg`, { type: 'image/jpeg' });
                        const upRes = await uploadImageToLibrary({ file, accessToken: tokens.accessToken });
                        if (upRes.ok && (upRes.data?.url || upRes.data?.cloudflareId)) {
                            mediaArr = [{
                                ...mediaArr[0],
                                url: upRes.data.url || mediaArr[0].url,
                                id: upRes.data.cloudflareId || mediaArr[0].id,
                                uid: upRes.data.cloudflareUid || mediaArr[0].uid,
                                productImageUrl: sourceImageUrl || mediaArr[0].productImageUrl,
                            }];
                        } else {
                            toast.error(isBundleMedia ? 'Bundle layout couldn’t be rendered — posting plain image.' : 'Frame couldn’t be applied — posting plain image.');
                        }
                    }
                } catch (err) {
                    console.warn('Compositing failed:', err);
                    toast.error(isBundleMedia ? 'Bundle layout couldn’t be rendered — posting plain image.' : 'Frame couldn’t be applied — posting plain image.');
                }
            }
        }

        // Build UTC scheduledAt from week-relative dayOffset + hour. The browser's
        // local TZ is authoritative — Date.toISOString() converts to UTC correctly.
        const date = addDays(weekStart, data.dayOffset);
        const [sh, sm] = hourToHM(data.hour);
        date.setHours(sh, sm, 0, 0);
        const scheduledAtISO = date.toISOString();

        const existing = data.id ? scheduled.find((p) => p.id === data.id) : null;

        // For local cache + optimistic state we want to preserve bundle data
        // (the server payload below stays untouched — it doesn't need it).
        const localMedia = mediaArr[0] && isBundleMedia ? {
            ...mediaArr[0],
            isBundle: true,
            bundle: libItem.bundle,
        } : mediaArr[0];

        // For bundle posts, populate linkInBio with the public checkout URL so
        // the auto-DM dispatcher can attach a "Shop Now" button card. Without
        // this, the dispatcher falls back to the post's image URL which isn't
        // a useful tap target for the recipient.
        const bundleCode = isBundleMedia ? (libItem?.bundle?.code || '') : '';
        const bundlePublicUrl = bundleCode
            ? `${(getSeoBaseUrl() || '').replace(/\/$/, '')}/c/${bundleCode}`
            : null;

        if (existing?.serverId) {
            // --- Edit flow (PUT) ---
            const previous = existing;
            // Persist frame + media choice in the local side-table (server may
            // not roundtrip these reliably).
            setFrameForPost(existing.serverId, data.frameId);
            if (localMedia) setMediaForPost(existing.serverId, localMedia);
            // Optimistic local update so the user sees the change immediately.
            setScheduled((prev) => prev.map((p) => (p.id === data.id ? {
                ...p,
                caption: data.caption,
                dayOffset: data.dayOffset,
                hour: data.hour,
                scheduledAt: scheduledAtISO,
                media: localMedia || p.media,
                mediaId: data.mediaId,
                frameId: data.frameId || 'none',
            } : p)));
            const res = await updateSocialPost({
                id: existing.serverId,
                accessToken: tokens.accessToken,
                platform: 'instagram',
                status: 'scheduled',
                caption: data.caption,
                media: mediaArr,
                scheduledAt: scheduledAtISO,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                ...(bundlePublicUrl ? { linkInBio: bundlePublicUrl } : {}),
            });
            if (!res.ok) {
                // Rollback + reload to stay consistent with the server.
                setScheduled((prev) => prev.map((p) => (p.id === previous.id ? previous : p)));
                toast.error(res.error || 'Failed to update post');
                loadScheduledPosts();
            } else {
                toast.success('Post updated');
            }
            return;
        }

        // --- New post flow (POST) ---
        // Optimistic insert with a temp id; swap for server-shaped entry on success.
        const tempId = `p${Date.now()}`;
        const optimistic = {
            id: tempId,
            serverId: null,
            status: 'scheduled',
            caption: data.caption,
            mediaId: data.mediaId,
            media: localMedia || null,
            dayOffset: data.dayOffset,
            hour: data.hour,
            scheduledAt: scheduledAtISO,
            frameId: data.frameId || 'none',
        };
        setScheduled((prev) => [...prev, optimistic]);
        setJustDroppedId(tempId);
        setTimeout(() => setJustDroppedId((cur) => (cur === tempId ? null : cur)), 700);

        const res = await createSocialPost({
            accessToken: tokens.accessToken,
            platform: 'instagram',
            status: 'scheduled',
            caption: data.caption,
            media: mediaArr,
            scheduledAt: scheduledAtISO,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(bundlePublicUrl ? { linkInBio: bundlePublicUrl } : {}),
        });
        if (!res.ok) {
            setScheduled((prev) => prev.filter((p) => p.id !== tempId));
            toast.error(res.error || 'Failed to schedule post');
            return;
        }
        const serverPost = res.data?.post;
        if (serverPost) {
            // Persist frame + media keyed by the new serverId so they survive reload
            // (the social_post table doesn't have columns for these fields yet).
            setFrameForPost(serverPost.id, data.frameId);
            if (localMedia) setMediaForPost(serverPost.id, localMedia);
            setScheduled((prev) => prev.map((p) => {
                if (p.id !== tempId) return p;
                const local = serverPostToLocal(serverPost, weekStart);
                return {
                    ...local,
                    media: local.media || localMedia || p.media,
                    frameId: data.frameId || 'none',
                };
            }));
        }
        toast.success('Post scheduled!');
    };
    const openComposer = (media) => { setComposerMedia(media || null); setComposerInitialDay(null); setComposerInitialHour(null); setComposerOpen(true); };
    const openEditor = (post) => { setEditingPost(post); setComposerInitialDay(null); setComposerInitialHour(null); setComposerOpen(true); };
    const closeComposer = () => { setComposerOpen(false); setEditingPost(null); setComposerMedia(null); setComposerInitialDay(null); setComposerInitialHour(null); };

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <style>{`
                @keyframes igPaperFlutter { 0% { transform: translateY(-22px) rotate(-8deg) scale(1.08); opacity: 0; } 35% { transform: translateY(-8px) rotate(4deg) scale(1.03); opacity: 1; } 65% { transform: translateY(-2px) rotate(-2deg) scale(1.01); } 100% { transform: translateY(0) rotate(0) scale(1); opacity: 1; } }
                @keyframes igSheetUp { 0% { transform: translateY(100%); opacity: 0.7; } 100% { transform: translateY(0); opacity: 1; } }
                @keyframes igSheetDown { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(100%); opacity: 0.7; } }
                @keyframes igPostZoomIn { 0% { opacity: 0; transform: scale(0.7); } 62% { opacity: 1; } 100% { opacity: 1; transform: scale(1); } }
                @keyframes igPostZoomOut { 0% { opacity: 1; transform: scale(1); } 18% { opacity: 1; } 100% { opacity: 0; transform: scale(0.05); } }
                @keyframes igBackdropFade { 0% { opacity: 0; } 100% { opacity: 1; } }
                @keyframes igBackdropFadeOut { 0% { opacity: 1; } 100% { opacity: 0; } }
                @keyframes igSpin { to { transform: rotate(360deg); } }
                @keyframes igConnectIn { 0% { opacity: 0; transform: translate(-50%, -50%) scale(0.92); } 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); } }

                /* Slim, muted scrollbars for library + calendar panes. Match the
                   stone palette so they feel native to the UI. Firefox uses the
                   standard properties; WebKit/Blink pseudo-elements cover Chrome/
                   Safari/Edge. Track stays fully transparent so the scrollbar
                   doesn't create a visible gutter. */
                .ig-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: #d6d3d1 transparent;
                }
                .ig-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
                .ig-scroll::-webkit-scrollbar-track { background: transparent; }
                .ig-scroll::-webkit-scrollbar-thumb {
                    background: #d6d3d1;
                    border-radius: 99px;
                    border: 2px solid transparent;
                    background-clip: padding-box;
                    transition: background 160ms ease;
                }
                .ig-scroll::-webkit-scrollbar-thumb:hover { background: #a8a29e; background-clip: padding-box; border: 2px solid transparent; }
                .ig-scroll::-webkit-scrollbar-thumb:active { background: #78716c; background-clip: padding-box; border: 2px solid transparent; }
                .ig-scroll::-webkit-scrollbar-corner { background: transparent; }
            `}</style>

            {/* Connect modal — cannot dismiss until connected */}
            {showConnectModal && (
                <ConnectModal onConnect={onInstagramConnect} onClose={() => {}} locked busy={instagramBusy} />
            )}

            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '16px 17px 16px' }}>
                <div>
                    <h1 style={{ fontSize: 34, fontWeight: 500, color: '#1f2937', margin: 0, letterSpacing: '-0.03em', lineHeight: 1 }}>Plan the week.</h1>
                </div>
                <div style={{ fontSize: 12, color: '#78716c', fontStyle: 'italic' }}>Click a tile to compose · drag onto a time slot to schedule</div>
            </div>

            {/* Visual separator between header and Library/Calendar layout */}
            <div style={{ height: 1, background: '#e7e5e4', margin: '0 17px' }} />

            {/* Main layout: Library + Calendar */}
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                {/* Library panel — fixed header/toggle/search, only the media grid scrolls. */}
                <div style={{
                    width: 240,
                    flexShrink: 0,
                    borderRight: '1px solid #e7e5e4',
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                    height: 'calc(100vh - 90px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    <LibraryPanel onPickMedia={openComposer} onNewPost={() => openComposer(null)}
                        draggingId={draggingMedia?.id} onDragStart={onMediaDragStart} onDragEnd={onMediaDragEnd}
                        libraryItems={libraryItems}
                        onUploadFiles={handleUploadFiles}
                        onDeleteItem={handleDeleteLibraryItem}
                        uploading={uploading}
                        loadingLibrary={libraryLoading}
                        loadingMoreLibrary={libraryLoadingMore}
                        hasMoreLibrary={libraryHasMore}
                        onLoadMoreLibrary={loadMoreLibrary}
                        accessToken={tokens?.accessToken}
                        onAddGiphyToLibrary={handleAddGiphyToLibrary} />
                </div>

                {/* Calendar panel — toolbar fixed at top, grid scrolls inside. */}
                <div style={{
                    flex: 1,
                    minWidth: 0,
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                    height: 'calc(100vh - 90px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    <div style={{ flexShrink: 0 }}>
                        <CalendarToolbar weekStart={weekStart} goPrev={goPrev} goNext={goNext} goToday={goToday}
                            calendarMode={calendarMode} setCalendarMode={setCalendarMode} />
                    </div>
                    <div className="ig-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                        {calendarMode === 'feed' ? (
                            <FeedView weekStart={weekStart} scheduled={scheduled} mediaMap={mediaMap}
                                dragOverCell={dragOverCell} onCellDragOver={onCellDragOver} onCellDrop={onCellDrop}
                                setDragOverCell={setDragOverCell} isDragActive={!!draggingMedia}
                                justDroppedId={justDroppedId} onEdit={openEditor} onDelete={deletePost} />
                        ) : (
                            <StoriesView weekStart={weekStart} stories={stories}
                                draggingMedia={draggingMedia}
                                onDropStory={onStoryDrop} onPickStory={onPickStory} onAddStory={onAddStory}
                                accountHandle={instagramHandle} accountAvatarUrl={instagramAvatarUrl} />
                        )}
                    </div>
                </div>
            </div>

            {/* Composer bottom sheet — full screen, outside the card */}
            {(composerOpen || editingPost) && (
                <ComposerSheet post={editingPost} media={composerMedia} weekStart={weekStart} mediaMap={mediaMap}
                    onClose={closeComposer} onSave={savePost} onDelete={deletePost}
                    allMedia={libraryItems}
                    initialDayOffset={composerInitialDay}
                    initialHour={composerInitialHour}
                    accountHandle={instagramHandle}
                    accountDisplayName={instagramDisplayName}
                    accountAvatarUrl={instagramAvatarUrl} />
            )}

            {/* Story composer bottom sheet — full screen */}
            {storyComposerOpen && (
                <StoryComposerSheet
                    story={editingStory}
                    media={storyComposerMedia}
                    dayOffset={storyComposerDay}
                    weekStart={weekStart}
                    onClose={closeStoryComposer}
                    onSave={(data) => { saveStory(data); closeStoryComposer(); }}
                    onDelete={() => { if (editingStory) deleteStory(editingStory.dayOffset, editingStory.id); closeStoryComposer(); }}
                    accountHandle={instagramHandle}
                    accountDisplayName={instagramDisplayName}
                    accountAvatarUrl={instagramAvatarUrl}
                />
            )}
        </div>
    );
}
